/**
 * Gói liệu trình + ví trả trước ở web quầy (B5.13). Bốn việc, mỗi màn một hành động chính:
 *   1. Thẻ khách: số dư ví + từng gói. Chính: "Bán gói"; phụ: "Nạp ví".
 *   2. Bán gói: chọn gói → hình thức → "Thu 3.500.000đ" (3 chạm).
 *   3. Nạp ví: số nhanh hoặc gõ → máy chủ tính tiền tặng → "Nạp 5.000.000đ" (3 chạm).
 *   4. Hoàn tất lịch: gói hạn gần nhất CHỌN SẴN, công tắc "Trả bằng ví" → "Xong" (2 chạm).
 * Hoàn / tặng / gia hạn / sửa sai chỉ vai có `hoan-dao-tien`; nhập số dư cũ chỉ chủ tiệm.
 *
 * Tiền đi dạng chuỗi số nguyên đồng (ADR-026) — giao diện KHÔNG cộng trừ tiền, trừ tổng hiển thị qua
 * `congVndChuoi`. Số dư mới luôn lấy từ phản hồi của máy chủ, kèm `ledgerSeq`.
 */
import {
  congVndChuoi,
  formatDuration,
  formatVnDate,
  type GoiMauGoiY,
  goiYGiaTriSoCu,
  maGoi,
  ngayVnCua,
  parseIsoStrict,
  thuTuTra,
  tinhGiamUuDai,
  vnd,
} from '@sapa/shared'
import {
  ArrowLeft,
  CalendarPlus,
  Check,
  CircleDollarSign,
  Gift,
  History,
  Package,
  Pencil,
  Percent,
  Plus,
  RotateCcw,
  ScrollText,
  Search,
  ShoppingBag,
  TicketPercent,
  Trash2,
  Undo2,
  UserPlus,
  UserRound,
  Wallet,
  X,
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type CustomerHit,
  chuanHoaSdt,
  type DongNhatKyTien,
  type DongSoCai,
  dieuKhoanTuChuoi,
  type GoiKhach,
  type GoiMau,
  type GoiPhuHop,
  type GoiViCuaKhach,
  type HinhThucThu,
  hienSdt,
  type MucTangVi,
  ngayVn,
  type SanPhamKho,
  type ServiceOption,
  type SoDuCuDaCo,
  type ToiLaAi,
  tenKhach,
  themNgay,
  tienVnd,
} from './api.ts'
import { CacLanDen } from './BaoCao.tsx'
import { TheChamKhach, TheChoCho } from './ChamKhach.tsx'
import { type ChiaTien, cacPhanChia, chiaHopLe, PhanTraTaiQuay } from './ChiaTra.tsx'
import { useTinhNang } from './cai-dat-tiem.ts'
import { chiNhanhChoHoSoKhach } from './chi-nhanh-dang-dung.ts'
import { DongTenTiem, useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { ManGopChon, ManGopXem } from './GopKhach.tsx'
import { nutGoi } from './goi-vi-nut-goi.ts'
import { KhoiLuuY, ManSuaKhach, ToChonThem, ToThemKhach } from './Khach.tsx'
import { ChonSanPham, dsChon, tongSanPham } from './Kho.tsx'
import { dienSanTuO, type FormKhach, giuSoLienMach, quyenKhach } from './khach-form.ts'
import { mocGanDay } from './khach-gan-day.ts'
import { NhapKhach } from './NhapKhach.tsx'
import { ngayTuChuSo } from './ngay-nhap.ts'
import { loiGiaTriForm, thanSoDuCu } from './so-du-cu.ts'
import { PhanTienBo } from './TienBo.tsx'
import { C as CK } from './text/cham-khach.ts'
import { G } from './text/goi-vi.ts'
import { GK } from './text/gop-khach.ts'
import { KH } from './text/khach.ts'
import { K } from './text/kho.ts'
import { T } from './text/vi.ts'
import { type BoNhap, boGui, boHopLe, boMoi } from './tien-bo.ts'
import { CacCum, Rong, ThongBao, To, Xuong } from './ui.tsx'
import { TheXacNhanZalo } from './XacNhanZalo.tsx'

const HINH_THUC: HinhThucThu[] = ['cash', 'transfer', 'card']
const NAP_NHANH = ['1000000', '2000000', '5000000', '10000000']

/** Dòng phụ của "Khách vừa đặt hoặc vừa tới": "Tới dd/MM" hoặc "Đặt lịch dd/MM" (dd/MM — năm làm dòng bẻ ở khổ 360). */
function cauMocGanDay(k: {
  lastVisitAt?: string | null
  datLichLuc?: string | null
}): string | null {
  const m = mocGanDay(k)
  if (m === null) return null
  const ngay = formatVnDate(parseIsoStrict(m.iso)).slice(0, 5)
  return m.loai === 'dat' ? G.bookedOn(ngay) : G.visitedOn(ngay)
}

/** Ngày cuối còn dùng được (giờ VN) của một mốc hết hiệu lực: "21/09/2027". */
function hanDen(iso: string): string {
  const d = ngayVnCua(new Date(Date.parse(iso) - 1000))
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`
}

/** "2027-12-31" → "31/12/2027". */
export function ngayViet(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
}

/** Mốc thời gian → "22/09/2026 14:05" giờ VN (sổ cái, nhật ký). */
export function lucViet(iso: string): string {
  const d = new Date(iso)
  const ngay = ngayVnCua(d)
  const gio = d.toLocaleTimeString('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${ngayViet(ngay)} ${gio}`
}

/**
 * Ô nhập ngày kiểu Việt "dd/mm/yyyy". KHÔNG dùng `<input type="date">`: Chrome hiện theo ngôn ngữ
 * máy (nhiều tablet đặt tiếng Anh → "10/22/2027"), chủ tiệm đọc nhầm ngày/tháng (ui-critic vòng 9).
 */
export function ONgay({
  id,
  giaTri,
  onDoi,
  khoang,
  goiY,
  goiYKhiDung,
  loiSai,
  onHopLe,
}: {
  id: string
  /** "yyyy-mm-dd" hoặc '' (đang gõ dở / sai). */
  giaTri: string
  onDoi: (iso: string) => void
  /** Khoảng năm hợp lệ — mặc định 2000–2100 (hạn gói). */
  khoang?: { tuNam: number; denNam: number }
  goiY?: string
  /** Câu dưới ô khi ngày đúng — mặc định "Đến hết ngày …" (hạn gói). */
  goiYKhiDung?: (ngay: string) => string
  /** Câu khi ngày sai theo loại (năm ngoài khoảng / ngày không có thật) — mặc định "Ngày chưa đúng…". */
  loiSai?: (loai: 'nam' | 'ngay') => string
  /** true khi ô trống hẳn hoặc là ngày đúng — false khi đang gõ dở / sai (không cho lưu). */
  onHopLe?: (ok: boolean) => void
}) {
  const [chu, setChu] = useState(giaTri === '' ? '' : ngayViet(giaTri))
  // Nút chọn nhanh đổi giá trị từ ngoài → hiện lại cho khớp.
  useEffect(() => {
    if (giaTri !== '') setChu(ngayViet(giaTri))
  }, [giaTri])
  const so = chu.replace(/\D/g, '')
  const sai = so.length === 8 && ngayTuChuSo(so, khoang) === null
  const nam = so.length === 8 ? Number.parseInt(so.slice(4), 10) : 0
  const namNgoaiKhoang = nam < (khoang?.tuNam ?? 2000) || nam > (khoang?.denNam ?? 2100)
  return (
    <>
      <input
        id={id}
        className={`o-nhap o-ngay ${sai ? 'o-sai' : ''}`}
        aria-invalid={sai}
        inputMode="numeric"
        autoComplete="off"
        placeholder={G.datePlaceholder}
        aria-describedby={`${id}-goi-y`}
        value={chu}
        onChange={(e) => {
          const s = e.target.value.replace(/\D/g, '').slice(0, 8)
          const hien = [s.slice(0, 2), s.slice(2, 4), s.slice(4, 8)]
            .filter((x) => x !== '')
            .join('/')
          setChu(hien)
          const iso = ngayTuChuSo(s, khoang)
          onDoi(iso ?? '')
          onHopLe?.(s === '' || iso !== null)
        }}
      />
      <p id={`${id}-goi-y`} className={`chu-phu ${sai ? 'chu-loi' : ''}`}>
        {sai
          ? loiSai === undefined
            ? G.dateWrong
            : loiSai(namNgoaiKhoang ? 'nam' : 'ngay')
          : giaTri !== ''
            ? (goiYKhiDung ?? G.dateUntil)(ngayViet(giaTri))
            : (goiY ?? G.dateHint)}
      </p>
    </>
  )
}

/** "5000000" → "5.000.000" để hiện trong ô nhập (không kèm "đ"). */
function nhomSo(so: string): string {
  return so === '' ? '' : tienVnd(so).replace(/đ$/, '')
}

export const loiCua = (e: unknown) => (e instanceof ApiFailure ? e.messageVi : T.serverFailed)

/**
 * Một khoá chống lặp cho MỘT thao tác tiền: giữ nguyên qua mọi lần bấm lại (rớt mạng), chỉ đổi sau khi
 * máy chủ báo thành công. Xem `gui()` ở api.ts.
 */
export function useKhoaIdem(): [string, () => void] {
  const [khoa, setKhoa] = useState(() => crypto.randomUUID())
  return [khoa, () => setKhoa(crypto.randomUUID())]
}

/** Ô nhập tiền: chỉ giữ chữ số, hiện có dấu chấm nhóm nghìn. */
export function OTien({
  id,
  giaTri,
  onDoi,
  goiY,
  khoa = false,
}: {
  id: string
  giaTri: string
  onDoi: (so: string) => void
  goiY?: string
  /** Không sửa được (ví dụ ưu đãi đã có khách nhận) — hiện rõ là ô khoá, không phải gõ không ăn. */
  khoa?: boolean
}) {
  // Đơn vị "đ" đứng cuối ô (ui-critic v21–25: "150.000" không rõ là tiền gì).
  return (
    <span className="o-tien-boc">
      <input
        id={id}
        className="o-nhap o-tien"
        inputMode="numeric"
        autoComplete="off"
        placeholder={goiY}
        disabled={khoa}
        value={nhomSo(giaTri)}
        onChange={(e) =>
          onDoi(
            e.target.value
              .replace(/\D/g, '')
              .replace(/^0+(?=\d)/, '')
              .slice(0, 11),
          )
        }
      />
      <span className="don-vi" aria-hidden="true">
        đ
      </span>
    </span>
  )
}

// =================================================================== Thẻ gói & ví của khách

type Man =
  | { ma: 'tong' }
  | { ma: 'ban-goi' }
  | { ma: 'nap-vi' }
  | { ma: 'hoan-goi'; goi: GoiKhach }
  | { ma: 'tang-buoi'; goi: GoiKhach }
  | { ma: 'gia-han'; goi: GoiKhach }
  | { ma: 'huy-so-cu'; goi: GoiKhach }
  | { ma: 'hoan-vi' }
  | { ma: 'lich-su'; loai: 'goi' | 'vi'; goi?: GoiKhach }
  | { ma: 'so-du-cu' }
  | { ma: 'sua-khach'; focus: 'ten' | 'luuY' }
  // STT 2.5 (ADR-091): gộp với hồ sơ khác — chọn hồ sơ còn lại, rồi xem trước + xác nhận.
  | { ma: 'gop-chon' }
  | { ma: 'gop-xem'; khacId: string }

export function ToGoiViKhach({
  api,
  toi,
  customerId,
  branchId,
  onDong,
  onMoKhac,
  onDaGop,
  baoBanDau,
}: {
  api: Api
  toi: ToiLaAi
  customerId: string
  branchId: string
  onDong: () => void
  /** Câu báo hiện ngay đầu hồ sơ rồi tự tắt sau ~5 giây (vừa thêm khách: "Đã thêm… Số cũ đã đổi sang…"). */
  baoBanDau?: string
  /**
   * Mở hồ sơ khác (trùng số khi sửa; hồ sơ đang mở vừa được gộp vào hồ sơ khác) — người gọi đổi `customerId`.
   * Không có thì không hiện nút "Mở hồ sơ".
   */
  onMoKhac?: (id: string) => void
  /** Vừa gộp hồ sơ xong: lịch / danh sách phía sau (đang hiện tên hồ sơ cũ) nạp lại. */
  onDaGop?: () => void
}) {
  const choHoan = toi.quyen.includes('hoan-dao-tien')
  const laChu = toi.toi.vai === 'owner'
  // STT 2.5: gộp hồ sơ trùng = chủ tiệm + quản lý (quyền `gop-khach`, máy chủ kiểm lại).
  const coGop = toi.quyen.includes('gop-khach')
  const quyenK = quyenKhach({ vai: toi.toi.vai, quyen: toi.quyen })
  const [data, setData] = useState<GoiViCuaKhach | null>(null)
  const [man, setManGoc] = useState<Man>({ ma: 'tong' })
  const [loi, setLoi] = useState<string | null>(null)
  /** Đổi màn con thì lỗi của màn cũ không được dính sang (ui-critic vòng 10). */
  const setMan = useCallback((m: Man) => {
    setLoi(null)
    setManGoc(m)
  }, [])
  const [bao, setBao] = useState<string | null>(baoBanDau ?? null)
  // Câu báo mở đầu (vừa thêm khách) tự tắt sau 5 giây — không treo mãi trên hồ sơ.
  useEffect(() => {
    if (baoBanDau === undefined) return
    const t = setTimeout(() => setBao((c) => (c === baoBanDau ? null : c)), 5000)
    return () => clearTimeout(t)
  }, [baoBanDau])

  const nap = useCallback(async () => {
    try {
      const moi = await api.goiViKhach(customerId)
      // Không lùi về số cũ: phản hồi đến muộn có `ledgerSeq` ví nhỏ hơn thì bỏ qua (luật ledgerSeq).
      setData((cu) =>
        cu !== null && BigInt(moi.vi.ledgerSeq) < BigInt(cu.vi.ledgerSeq) ? cu : moi,
      )
    } catch (e) {
      // Hồ sơ này vừa được GỘP vào hồ sơ khác (mở từ danh sách cũ / máy khác gộp): mở thẳng hồ sơ còn lại.
      if (e instanceof ApiFailure && e.hoSoGiuId !== null && onMoKhac !== undefined) {
        setBao(GK.daGopChuyen)
        onMoKhac(e.hoSoGiuId)
        return
      }
      setLoi(loiCua(e))
    }
  }, [api, customerId, onMoKhac])

  useEffect(() => {
    void nap()
  }, [nap])

  function xong(thongBao: string) {
    setBao(thongBao)
    setMan({ ma: 'tong' })
    void nap()
  }
  // ADR-074: tắt thì không nhắc gói / ví / điểm ở tờ khách (số dư còn thì vẫn hiện ở từng mục).
  const coGoiVi = useTinhNang('goi-vi')
  const coDiemTn = useTinhNang('diem')

  const tieuDe =
    man.ma === 'ban-goi'
      ? G.sellTitle
      : man.ma === 'nap-vi'
        ? G.topUpTitle
        : man.ma === 'hoan-goi'
          ? G.refundTitle
          : man.ma === 'tang-buoi'
            ? G.grantTitle
            : man.ma === 'gia-han'
              ? G.extendTitle
              : man.ma === 'huy-so-cu'
                ? G.cancelOpeningTitle
                : man.ma === 'hoan-vi'
                  ? G.refundWalletTitle
                  : man.ma === 'lich-su'
                    ? G.historyTitle
                    : man.ma === 'so-du-cu'
                      ? G.openingTitle
                      : man.ma === 'sua-khach'
                        ? KH.suaKhach
                        : man.ma === 'gop-chon'
                          ? GK.tieuDeChon
                          : man.ma === 'gop-xem'
                            ? GK.tieuDeXem
                            : coGoiVi
                              ? G.cardTitle
                              : G.cardTitleGon

  return (
    <To nhan={tieuDe} onDong={onDong}>
      <header className="to-dau">
        {man.ma !== 'tong' && (
          <button
            type="button"
            className="nut nut-nhat nut-icon nut-icon-lon"
            aria-label={G.back}
            onClick={() => setMan(man.ma === 'gop-xem' ? { ma: 'gop-chon' } : { ma: 'tong' })}
          >
            <ArrowLeft size={22} />
          </button>
        )}
        {man.ma === 'tong' ? (
          // Hồ sơ: tên khách là tiêu đề, kèm số điện thoại — chủ tiệm biết ngay mình đang mở ai.
          <div className="dau-ho-so">
            <span className="nhan-ho-so">{G.cardTitleGon}</span>
            <h2>{data === null ? '…' : tenKhach(data.khach.ten)}</h2>
            <p className="so-ho-so">
              {data === null
                ? ''
                : data.khach.sdt === null || data.khach.sdt === undefined
                  ? KH.chuaCoSo
                  : hienSdt(data.khach.sdt)}
            </p>
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>{tieuDe}</h2>
            <p className="chu-phu">{data === null ? '…' : tenKhach(data.khach.ten)}</p>
          </div>
        )}
        {man.ma === 'tong' && quyenK.coThem && data !== null && data.khach.daXoa !== true && (
          <button
            type="button"
            className="nut nut-nhat nut-icon nut-icon-lon"
            aria-label={KH.suaKhach}
            onClick={() => setMan({ ma: 'sua-khach', focus: 'ten' })}
          >
            <Pencil size={20} />
          </button>
        )}
        <button
          type="button"
          className="nut nut-nhat nut-icon nut-icon-lon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>

      {loi !== null && (
        <div className="hop-loi-to">
          <ThongBao loai="loi" onDong={() => setLoi(null)}>
            {loi}
          </ThongBao>
        </div>
      )}

      {man.ma === 'tong' && (
        <TongQuan
          data={data}
          choHoan={choHoan}
          laChu={laChu}
          coGop={coGop && data?.khach.daXoa !== true}
          bao={bao}
          onBao={setBao}
          onMan={setMan}
          dauTo={
            data === null ? null : (
              <>
                <KhoiLuuY luuY={data.khach.luuY} />
                {(data.khach.luuY ?? '').trim() === '' &&
                  quyenK.coThem &&
                  data.khach.daXoa !== true && (
                    <button
                      type="button"
                      className="nut nut-vien nut-form nut-them-luu-y"
                      onClick={() => setMan({ ma: 'sua-khach', focus: 'luuY' })}
                    >
                      {KH.themLuuY}
                    </button>
                  )}
              </>
            )
          }
          phanPhu={
            data === null ? null : (
              <>
                <NgaySinhKhach
                  key={data.khach.ngaySinh ?? ''}
                  api={api}
                  customerId={customerId}
                  ngaySinh={data.khach.ngaySinh}
                  diem={coDiemTn ? (data.diem?.diem ?? null) : null}
                  onLoi={setLoi}
                  onXong={(cau) => {
                    setBao(cau)
                    void nap()
                  }}
                />
                <CacLanDen customerId={customerId} />
              </>
            )
          }
        />
      )}
      {man.ma === 'ban-goi' && (
        <BanGoi
          api={api}
          customerId={customerId}
          branchId={branchId}
          onLoi={setLoi}
          onXong={xong}
        />
      )}
      {man.ma === 'nap-vi' && (
        <NapVi api={api} customerId={customerId} branchId={branchId} onLoi={setLoi} onXong={xong} />
      )}
      {man.ma === 'hoan-goi' && (
        <HoanGoi api={api} goi={man.goi} branchId={branchId} onLoi={setLoi} onXong={xong} />
      )}
      {man.ma === 'tang-buoi' && <TangBuoi api={api} goi={man.goi} onLoi={setLoi} onXong={xong} />}
      {man.ma === 'gia-han' && <GiaHan api={api} goi={man.goi} onLoi={setLoi} onXong={xong} />}
      {man.ma === 'huy-so-cu' && <HuySoCu api={api} goi={man.goi} onLoi={setLoi} onXong={xong} />}
      {man.ma === 'hoan-vi' && data !== null && (
        <HoanVi
          api={api}
          customerId={customerId}
          branchId={branchId}
          vi={data.vi}
          onLoi={setLoi}
          onXong={xong}
        />
      )}
      {man.ma === 'lich-su' && (
        <LichSu
          api={api}
          customerId={customerId}
          loai={man.loai}
          {...(man.goi === undefined ? {} : { goi: man.goi })}
          choSua={choHoan}
          onLoi={setLoi}
          onDaSua={() => {
            setBao(G.reversed)
            void nap()
          }}
        />
      )}
      {man.ma === 'sua-khach' && data !== null && (
        <ManSuaKhach
          api={api}
          customerId={customerId}
          goc={{ ten: data.khach.ten, sdt: data.khach.sdt ?? null, luuY: data.khach.luuY ?? null }}
          coDoiSo={quyenK.coDoiSo}
          focus={man.focus}
          onLoi={setLoi}
          onMoHoSo={(id) => onMoKhac?.(id)}
          onXong={xong}
        />
      )}
      {man.ma === 'gop-chon' && (
        <ManGopChon
          api={api}
          customerId={customerId}
          onChon={(id) => setMan({ ma: 'gop-xem', khacId: id })}
          onLoi={setLoi}
        />
      )}
      {man.ma === 'gop-xem' && (
        <ManGopXem
          api={api}
          customerId={customerId}
          khacId={man.khacId}
          onLoi={setLoi}
          onDong={() => setMan({ ma: 'tong' })}
          onMoMucCho={onDong}
          onXong={(r, tenGiu, cauThem) => {
            onDaGop?.()
            const cau =
              cauThem === null
                ? GK.xong(tenKhach(tenGiu))
                : `${GK.xong(tenKhach(tenGiu))} ${cauThem}`
            // Hồ sơ đang mở là hồ sơ bị ẩn → chuyển sang hồ sơ giữ; còn lại thì nạp lại hồ sơ này.
            if (r.boId === customerId && onMoKhac !== undefined) {
              setBao(cau)
              onMoKhac(r.giuId)
              return
            }
            xong(cau)
          }}
        />
      )}
      {man.ma === 'so-du-cu' && (
        <SoDuCu
          api={api}
          customerId={customerId}
          branchId={branchId}
          onLoi={setLoi}
          onXong={xong}
          onHuy={() => setMan({ ma: 'tong' })}
        />
      )}
    </To>
  )
}

/** Ngày sinh để quầy chúc mừng (Pha E: danh sách "Sinh nhật 7 ngày tới"). Một ô, một nút. */
function NgaySinhKhach({
  api,
  customerId,
  ngaySinh,
  diem,
  onLoi,
  onXong,
}: {
  api: Api
  customerId: string
  ngaySinh: string | null
  /** Điểm thưởng khách đang có (null = tiệm chưa bật điểm). */
  diem: string | null
  onLoi: (s: string) => void
  onXong: (cau: string) => void
}) {
  const [giaTri, setGiaTri] = useState(ngaySinh ?? '')
  const [hopLe, setHopLe] = useState(true)
  const [dang, setDang] = useState(false)
  // Ô đang sai / gõ dở thì KHÔNG cho lưu (trước đây lưu '' = xoá ngày sinh — ui-critic v14).
  const doi = hopLe && giaTri !== (ngaySinh ?? '')
  const namNay = Number.parseInt(ngayVn().slice(0, 4), 10)
  async function luu() {
    setDang(true)
    try {
      await api.luuNgaySinh(customerId, giaTri === '' ? null : giaTri)
      onXong(CK.daLuuNgaySinh)
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <div className="hang-ngay-sinh">
      <div className="truong">
        <div className="dau-ngay-sinh">
          <label className="nhan" htmlFor="ngay-sinh-khach">
            {CK.ngaySinh}
          </label>
          {diem !== null && (
            <span className="diem-the-khach">
              <Gift size={16} />
              {CK.coDiem(diem)}
            </span>
          )}
        </div>
        <ONgay
          id="ngay-sinh-khach"
          giaTri={giaTri}
          onDoi={setGiaTri}
          khoang={{ tuNam: 1920, denNam: namNay }}
          goiY={CK.ngaySinhGoiY}
          goiYKhiDung={CK.sinhNgay}
          loiSai={CK.ngaySinhSai}
          onHopLe={setHopLe}
        />
      </div>
      {doi && (
        <button type="button" className="nut nut-vien" disabled={dang} onClick={() => void luu()}>
          {giaTri === '' ? CK.xoaNgaySinh : CK.luuNgaySinh}
        </button>
      )}
    </div>
  )
}

function TongQuan({
  data,
  choHoan,
  laChu,
  coGop,
  bao,
  onBao,
  onMan,
  dauTo,
  phanPhu,
}: {
  data: GoiViCuaKhach | null
  choHoan: boolean
  laChu: boolean
  /** STT 2.5: chủ tiệm / quản lý → có nút "Gộp với hồ sơ khác". */
  coGop: boolean
  bao: string | null
  onBao: (s: string | null) => void
  onMan: (m: Man) => void
  /** "Lưu ý cố định" (và nút thêm): đầu vùng cuộn, trên ví / gói — điều cần nhớ về khách (ADR-089). */
  dauTo?: ReactNode
  /** Ngày sinh + điểm: trong vùng cuộn, dưới ví / gói (việc phụ dưới việc chính). */
  phanPhu?: ReactNode
}) {
  // ADR-074: tắt "Gói và ví" thì không có "Nạp ví" / "Bán gói" — nhưng khách CÒN tiền trong ví / buổi
  // trong gói thì vẫn hiện (tiền của khách không được "biến mất" khỏi màn hình vì một công tắc).
  const batVi = useTinhNang('goi-vi')
  if (data === null) {
    return (
      <div className="to-than">
        <Xuong cao={88} />
        <Xuong cao={64} />
        <Xuong cao={64} />
      </div>
    )
  }
  const coVi = data.vi.cashVnd !== '0' || data.vi.bonusVnd !== '0'
  return (
    <>
      <div className="to-than">
        {bao !== null && (
          <ThongBao loai="thanh-cong" lon onDong={() => onBao(null)}>
            {bao}
          </ThongBao>
        )}
        {dauTo}
        {batVi && <p className="chu-phu">{G.walletExplain}</p>}
        {(batVi || coVi) && (
          <section className="the-vi">
            <span className="vong-icon-nho">
              <Wallet size={20} />
            </span>
            <div className="the-vi-chu">
              <span className="nhan-muc">{G.wallet}</span>
              {coVi ? (
                <>
                  <b>{tienVnd(data.vi.cashVnd)}</b>
                  {data.vi.bonusVnd !== '0' && (
                    <span className="tang-them">{G.walletBonus(tienVnd(data.vi.bonusVnd))}</span>
                  )}
                </>
              ) : (
                <b className="mo">{G.walletEmpty}</b>
              )}
            </div>
            <button
              type="button"
              className="nut nut-nhat nut-nho"
              onClick={() => onMan({ ma: 'lich-su', loai: 'vi' })}
            >
              <History size={16} />
              {G.history}
            </button>
          </section>
        )}

        {(batVi || data.goi.length > 0) && (
          <div className="muc-to">
            <h3>{G.packages}</h3>
            {data.goi.length === 0 ? (
              <p className="chu-phu">{G.noPackage}</p>
            ) : (
              <ul className="ds-goi">
                {data.goi.map((g) => {
                  const nut = nutGoi({
                    batGoiVi: batVi,
                    choHoan,
                    laChu,
                    soCu: g.soCu === true,
                    sessionsUsed: g.sessionsUsed,
                    trangThai: g.trangThai,
                  })
                  return (
                    <li key={g.id} className={`the-goi ${g.trangThai === 'con' ? '' : 'het'}`}>
                      <div className="the-goi-dau">
                        <Package size={18} />
                        <b>{g.name}</b>
                        {g.tang === true && <span className="nhan-nho">{G.giftTag}</span>}
                        {g.trangThai !== 'con' && (
                          <span className="nhan-nho canh-bao">{G.trangThai[g.trangThai]}</span>
                        )}
                      </div>
                      <div className="thanh-buoi" aria-hidden="true">
                        <span
                          style={{
                            width: `${Math.round((g.sessionsLeft / Math.max(1, g.sessionsTotal)) * 100)}%`,
                          }}
                        />
                      </div>
                      <p className="chu-phu">
                        <CacCum
                          cac={[
                            G.sessionsLeft(g.sessionsLeft, g.sessionsTotal),
                            G.expiresOn(hanDen(g.expiresAt)),
                            G.packageCode(maGoi(g.id)),
                          ]}
                        />
                      </p>
                      <div className="hang-nut-goi">
                        <button
                          type="button"
                          className="nut nut-nhat nut-nho"
                          onClick={() => onMan({ ma: 'lich-su', loai: 'goi', goi: g })}
                        >
                          <History size={16} />
                          {G.history}
                        </button>
                        {/* ADR-074: "Tặng buổi" tạo thêm giá trị → tắt Gói và ví thì ẨN (máy chủ 403); các đường lấy tiền / buổi của khách RA vẫn còn. */}
                        {nut.tangBuoi && (
                          <button
                            type="button"
                            className="nut nut-nhat nut-nho"
                            onClick={() => onMan({ ma: 'tang-buoi', goi: g })}
                          >
                            <Plus size={16} />
                            {G.grantSessions}
                          </button>
                        )}
                        {nut.giaHan && (
                          <button
                            type="button"
                            className="nut nut-nhat nut-nho"
                            onClick={() => onMan({ ma: 'gia-han', goi: g })}
                          >
                            <CalendarPlus size={16} />
                            {G.extend}
                          </button>
                        )}
                        {nut.huySoCu && (
                          <button
                            type="button"
                            className="nut nut-nhat nut-nho nguy"
                            onClick={() => onMan({ ma: 'huy-so-cu', goi: g })}
                          >
                            <Trash2 size={16} />
                            {G.cancelOpening}
                          </button>
                        )}
                        {nut.hoanGoi && (
                          <button
                            type="button"
                            className="nut nut-nhat nut-nho nguy"
                            onClick={() => onMan({ ma: 'hoan-goi', goi: g })}
                          >
                            <Undo2 size={16} />
                            {G.refundPackage}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        {(choHoan || laChu || coGop) && (
          <div className="muc-to">
            <h3>{G.more}</h3>
            <div className="hang-chip">
              {choHoan && coVi && (
                <button
                  type="button"
                  className="nut nut-vien nut-phu-lon"
                  onClick={() => onMan({ ma: 'hoan-vi' })}
                >
                  <Undo2 size={16} />
                  {G.refundWallet}
                </button>
              )}
              {/* ADR-074: "Nhập số dư cũ" là việc của Gói và ví — tắt thì không hiện (máy chủ cũng chặn). */}
              {laChu && batVi && (
                <button
                  type="button"
                  className="nut nut-vien nut-phu-lon"
                  onClick={() => onMan({ ma: 'so-du-cu' })}
                >
                  {G.opening}
                </button>
              )}
              {coGop && (
                <button
                  type="button"
                  className="nut nut-vien nut-form gop-nut-mo"
                  onClick={() => onMan({ ma: 'gop-chon' })}
                >
                  {GK.nutMo}
                </button>
              )}
            </div>
          </div>
        )}
        {phanPhu}
      </div>
      {batVi && (
        <footer className="to-chan hai-nut">
          <button
            type="button"
            className="nut nut-vien nut-lon"
            onClick={() => onMan({ ma: 'nap-vi' })}
          >
            <Wallet size={18} />
            {G.topUp}
          </button>
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            onClick={() => onMan({ ma: 'ban-goi' })}
          >
            <Package size={18} />
            {G.sellPackage}
          </button>
        </footer>
      )}
    </>
  )
}

type Xong = (thongBao: string) => void

export function ChonHinhThuc({
  giaTri,
  onDoi,
  nhan = G.payMethod,
}: {
  giaTri: HinhThucThu
  onDoi: (h: HinhThucThu) => void
  nhan?: string
}) {
  return (
    <div className="muc-to">
      <h3>{nhan}</h3>
      <fieldset className="phan-doan phan-doan-deu phan-doan-phu" aria-label={nhan}>
        {HINH_THUC.map((h) => (
          <button
            key={h}
            type="button"
            className={giaTri === h ? 'dang-chon' : ''}
            aria-pressed={giaTri === h}
            onClick={() => onDoi(h)}
          >
            {/* Ô đang chọn có dấu ✓ — không chỉ khác màu nền (ui-critic vòng 41). */}
            {giaTri === h && <Check size={18} strokeWidth={2.4} />}
            {G.hinhThuc[h]}
          </button>
        ))}
      </fieldset>
    </div>
  )
}

function BanGoi({
  api,
  customerId,
  branchId,
  onLoi,
  onXong,
}: {
  api: Api
  customerId: string
  branchId: string
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [ds, setDs] = useState<GoiMau[] | null>(null)
  const [chon, setChon] = useState<GoiMau | null>(null)
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  const [dang, setDang] = useState(false)

  useEffect(() => {
    void api
      .goiMauDangBan()
      .then((r) => {
        setDs(r.goi)
        // Chỉ có một gói thì chọn sẵn: bớt một chạm.
        if (r.goi.length === 1) setChon(r.goi[0] ?? null)
      })
      .catch((e: unknown) => onLoi(loiCua(e)))
  }, [api, onLoi])

  async function thu() {
    if (chon === null) return
    setDang(true)
    try {
      await api.banGoi(customerId, { planId: chon.id, branchId, hinhThuc }, khoa)
      lamMoiKhoa()
      onXong(G.sold(chon.name))
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  return (
    <>
      <div className="to-than">
        <div className="muc-to">
          <h3>{G.pickPlan}</h3>
          {ds === null ? (
            <Xuong cao={60} />
          ) : ds.length === 0 ? (
            <Rong icon={<Package size={26} />} tieuDe={G.noPlan} moTa={G.noPlanHint} />
          ) : (
            <div className="ds-chon">
              {ds.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  aria-pressed={chon?.id === g.id}
                  className={`muc-chon ${chon?.id === g.id ? 'dang-chon' : ''}`}
                  onClick={() => setChon(g)}
                >
                  <span className="cham-chon" />
                  <span className="chu">
                    <b>{g.name}</b>
                    <span>
                      <CacCum
                        cac={[
                          g.dichVu,
                          G.planLine(g.paidSessions, g.bonusSessions),
                          G.planValidity(g.validityDays),
                        ]}
                      />
                    </span>
                  </span>
                  <span className="gia">{tienVnd(g.priceVnd)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <ChonHinhThuc giaTri={hinhThuc} onDoi={setHinhThuc} />
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={chon === null || dang}
          onClick={() => void thu()}
        >
          {chon === null ? G.pickPlanFirst : dang ? G.finishing : G.collect(tienVnd(chon.priceVnd))}
        </button>
      </footer>
    </>
  )
}

function NapVi({
  api,
  customerId,
  branchId,
  onLoi,
  onXong,
}: {
  api: Api
  customerId: string
  branchId: string
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [soTien, setSoTien] = useState('')
  const [tang, setTang] = useState<string | null>(null)
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  const [dang, setDang] = useState(false)
  const hopLe = soTien !== '' && soTien !== '0' && soTien.endsWith('000')

  // Tiền tặng do MÁY CHỦ tính theo mức chủ tiệm đặt — giao diện chỉ hiện.
  useEffect(() => {
    setTang(null)
    if (!hopLe) return
    const t = setTimeout(() => {
      void api
        .xemTruocNap(soTien)
        .then((r) => setTang(r.bonusVnd))
        .catch(() => setTang(null))
    }, 250)
    return () => clearTimeout(t)
  }, [api, soTien, hopLe])

  async function napTien() {
    setDang(true)
    try {
      await api.napVi(customerId, { soTienVnd: soTien, branchId, hinhThuc }, khoa)
      lamMoiKhoa()
      onXong(G.toppedUp(tienVnd(soTien)))
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  return (
    <>
      <div className="to-than">
        <div className="muc-to">
          <label className="nhan" htmlFor="so-tien-nap">
            {G.amount}
          </label>
          <OTien id="so-tien-nap" giaTri={soTien} onDoi={setSoTien} goiY={G.amountPlaceholder} />
          <div className="hang-chip" style={{ marginTop: 12 }}>
            {NAP_NHANH.map((x) => (
              <button
                key={x}
                type="button"
                className={`chip ${soTien === x ? 'dang-chon' : ''}`}
                onClick={() => setSoTien(x)}
              >
                {tienVnd(x)}
              </button>
            ))}
          </div>
          {hopLe && (
            <p className={`goi-y-tang ${tang !== null && tang !== '0' ? 'co' : ''}`}>
              {tang === null ? '…' : tang === '0' ? G.noBonus : G.bonusPreview(tienVnd(tang))}
            </p>
          )}
          <p className="chu-phu">{G.bonusNotRefundable}</p>
        </div>
        <ChonHinhThuc giaTri={hinhThuc} onDoi={setHinhThuc} />
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={!hopLe || dang}
          onClick={() => void napTien()}
        >
          {!hopLe ? G.enterAmount : dang ? G.finishing : G.topUpBtn(tienVnd(soTien))}
        </button>
      </footer>
    </>
  )
}

/** Tải hỏng: thay khung xương đứng mãi bằng một lối thoát (ui-critic vòng 10). */
function ThuLai({ onThuLai }: { onThuLai: () => void }) {
  return (
    <Rong
      icon={<RotateCcw size={26} />}
      tieuDe={G.loadFailed}
      hanhDong={
        <button type="button" className="nut nut-vien" onClick={onThuLai}>
          {T.retry}
        </button>
      }
    />
  )
}

export function OLyDo({
  giaTri,
  onDoi,
  goiY,
  ghiChu,
}: {
  giaTri: string
  onDoi: (s: string) => void
  goiY: string
  /**
   * Dòng phụ CỐ ĐỊNH dưới ô (không mất khi gõ, không bị cắt như chữ mờ). Có thì thay câu mặc định "Ghi lý do để lưu —
   * chủ tiệm sẽ xem lại" — câu đó đúng với lý do nội bộ (hoàn tiền, đảo sổ) nhưng NÓI NGƯỢC với lý do huỷ lịch, vốn
   * hiện cho khách (K1a vòng 2).
   */
  ghiChu?: string
}) {
  const thieu = giaTri.trim().length < 2
  return (
    <div className="truong" style={{ marginTop: 12 }}>
      <label className="nhan" htmlFor="ly-do">
        {G.reason}
      </label>
      <input
        id="ly-do"
        className="o-nhap"
        placeholder={goiY}
        value={giaTri}
        maxLength={300}
        aria-describedby="ly-do-goi-y"
        onChange={(e) => onDoi(e.target.value)}
      />
      {ghiChu !== undefined ? (
        <p id="ly-do-goi-y" className="chu-phu">
          {ghiChu}
        </p>
      ) : (
        thieu && (
          <p id="ly-do-goi-y" className="chu-phu">
            {G.reasonNeeded}
          </p>
        )
      )}
    </div>
  )
}

function HoanGoi({
  api,
  goi,
  branchId,
  onLoi,
  onXong,
}: {
  api: Api
  goi: GoiKhach
  /** Chi nhánh TRẢ tiền — két của chính chi nhánh này bị trừ (0022). */
  branchId: string
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [xem, setXem] = useState<{ toiDaVnd: string; daDung: number; giaLeVnd: string } | null>(
    null,
  )
  const [soTien, setSoTien] = useState('')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const [hong, setHong] = useState(false)
  const [lan, setLan] = useState(0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: `lan` là tín hiệu "Thử lại".
  useEffect(() => {
    setHong(false)
    void api
      .xemTruocHoan(goi.id)
      .then((r) => {
        setXem(r)
        setSoTien(r.toiDaVnd)
      })
      .catch((e: unknown) => {
        setHong(true)
        onLoi(loiCua(e))
      })
  }, [api, goi.id, onLoi, lan])

  const hopLe = xem !== null && soTien !== '' && soTien.endsWith('000') && lyDo.trim().length >= 2
  async function hoan() {
    setDang(true)
    try {
      const r = await api.hoanGoi(
        goi.id,
        { refundVnd: soTien, note: lyDo.trim(), branchId, hinhThuc },
        khoa,
      )
      lamMoiKhoa()
      onXong(G.refunded(tienVnd(r.traKhachVnd)))
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <>
      <div className="to-than">
        <p className="chu-phu">
          <b>{goi.name}</b> · {G.sessionsLeft(goi.sessionsLeft, goi.sessionsTotal)}
        </p>
        {xem === null && hong ? (
          <ThuLai onThuLai={() => setLan((n) => n + 1)} />
        ) : xem === null ? (
          <Xuong cao={60} />
        ) : (
          <div className="muc-to">
            <ThongBao loai="canh-bao">
              <b>{G.refundMax(tienVnd(xem.toiDaVnd))}</b>
              <div>{G.refundFormula(xem.daDung, tienVnd(xem.giaLeVnd))}</div>
            </ThongBao>
            {goi.soCu && goi.sessionsUsed > 0 && (
              <ThongBao loai="canh-bao">{G.refundSoCuDaDung(goi.sessionsUsed)}</ThongBao>
            )}
            <label className="nhan" htmlFor="tien-hoan" style={{ marginTop: 12, display: 'block' }}>
              {G.refundAmount}
            </label>
            <OTien id="tien-hoan" giaTri={soTien} onDoi={setSoTien} />
            <ChonHinhThuc giaTri={hinhThuc} onDoi={setHinhThuc} nhan={G.refundPayMethod} />
            <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonRefund} />
          </div>
        )}
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-nguy nut-lon"
          disabled={!hopLe || dang}
          onClick={() => void hoan()}
        >
          {xem === null ? G.notLoaded : G.refundBtn(tienVnd(soTien === '' ? '0' : soTien))}
        </button>
      </footer>
    </>
  )
}

function TangBuoi({
  api,
  goi,
  onLoi,
  onXong,
}: {
  api: Api
  goi: GoiKhach
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [so, setSo] = useState(1)
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  async function tang() {
    setDang(true)
    try {
      await api.tangBuoi(goi.id, { soBuoi: so, note: lyDo.trim() }, khoa)
      lamMoiKhoa()
      onXong(G.granted(so))
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <>
      <div className="to-than">
        <p className="chu-phu">
          <b>{goi.name}</b> · {G.sessionsLeft(goi.sessionsLeft, goi.sessionsTotal)}
        </p>
        <div className="muc-to">
          <h3>{G.grantCount}</h3>
          <div className="hang-chip">
            {[1, 2, 3, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={`chip ${so === n ? 'dang-chon' : ''}`}
                onClick={() => setSo(n)}
              >
                {n}
              </button>
            ))}
          </div>
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonGrant} />
        </div>
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={lyDo.trim().length < 2 || dang}
          onClick={() => void tang()}
        >
          {G.grantBtn(so)}
        </button>
      </footer>
    </>
  )
}

function GiaHan({
  api,
  goi,
  onLoi,
  onXong,
}: {
  api: Api
  goi: GoiKhach
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const cuoi = ngayVnCua(new Date(Date.parse(goi.expiresAt) - 1000))
  const [den, setDen] = useState(themNgay(cuoi > ngayVn() ? cuoi : ngayVn(), 30))
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  async function giaHan() {
    setDang(true)
    try {
      await api.giaHan(goi.id, { denNgay: den, note: lyDo.trim() }, khoa)
      lamMoiKhoa()
      onXong(G.extended)
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <>
      <div className="to-than">
        <p className="chu-phu">
          <b>{goi.name}</b> · {G.expiresOn(hanDen(goi.expiresAt))}
        </p>
        <div className="muc-to">
          <h3>{G.extendTo}</h3>
          <div className="hang-chip">
            {[1, 3, 6, 12].map((n) => {
              const d = themNgay(cuoi > ngayVn() ? cuoi : ngayVn(), n === 12 ? 365 : n * 30)
              return (
                <button
                  key={n}
                  type="button"
                  className={`chip ${den === d ? 'dang-chon' : ''}`}
                  aria-pressed={den === d}
                  onClick={() => setDen(d)}
                >
                  {G.extendMonths(n)}
                </button>
              )
            })}
          </div>
          <div className="truong" style={{ marginTop: 12 }}>
            <ONgay id="gia-han-den" giaTri={den} onDoi={setDen} />
          </div>
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonExtend} />
        </div>
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={lyDo.trim().length < 2 || den === '' || den <= cuoi || dang}
          onClick={() => void giaHan()}
        >
          {G.extendBtn}
        </button>
      </footer>
    </>
  )
}

function HuySoCu({
  api,
  goi,
  onLoi,
  onXong,
}: {
  api: Api
  goi: GoiKhach
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  async function huy() {
    setDang(true)
    try {
      await api.huyGoiSoCu(goi.id, { note: lyDo.trim() }, khoa)
      lamMoiKhoa()
      onXong(G.openingCancelled)
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <>
      <div className="to-than">
        <p className="chu-phu">
          <b>{goi.name}</b> · {G.sessionsLeft(goi.sessionsLeft, goi.sessionsTotal)}
        </p>
        <ThongBao loai="canh-bao">{G.cancelOpeningBody}</ThongBao>
        <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonCancel} />
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-nguy nut-lon"
          disabled={lyDo.trim().length < 2 || dang}
          onClick={() => void huy()}
        >
          {G.cancelOpeningBtn}
        </button>
      </footer>
    </>
  )
}

function HoanVi({
  api,
  customerId,
  branchId,
  vi,
  onLoi,
  onXong,
}: {
  api: Api
  customerId: string
  branchId: string
  vi: GoiViCuaKhach['vi']
  onLoi: (s: string) => void
  onXong: Xong
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  async function hoan() {
    setDang(true)
    try {
      const r = await api.hoanVi(customerId, { branchId, hinhThuc, note: lyDo.trim() }, khoa)
      lamMoiKhoa()
      onXong(G.walletRefunded(tienVnd(r.traKhachVnd)))
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <>
      <div className="to-than">
        <ThongBao loai="canh-bao">
          {G.refundWalletBody(tienVnd(vi.cashVnd), tienVnd(vi.bonusVnd))}
        </ThongBao>
        <ChonHinhThuc giaTri={hinhThuc} onDoi={setHinhThuc} nhan={G.refundPayMethod} />
        <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonWallet} />
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-nguy nut-lon"
          disabled={lyDo.trim().length < 2 || dang}
          onClick={() => void hoan()}
        >
          {G.refundWalletBtn(tienVnd(vi.cashVnd))}
        </button>
      </footer>
    </>
  )
}

function moTaDong(d: DongSoCai, loai: 'goi' | 'vi'): string {
  if (loai === 'vi') {
    const that = d.cashDeltaVnd ?? '0'
    const tang = d.bonusDeltaVnd ?? '0'
    const dau = (s: string) => (s.startsWith('-') ? `−${tienVnd(s.slice(1))}` : `+${tienVnd(s)}`)
    return tang === '0' ? dau(that) : `${dau(that)} (tặng ${dau(tang)})`
  }
  const buoi = d.sessionsDelta ?? 0
  const phan = [buoi === 0 ? '' : `${buoi > 0 ? '+' : '−'}${Math.abs(buoi)} buổi`]
  if (d.refundVnd !== undefined && d.refundVnd !== '0')
    phan.push(`trả khách ${tienVnd(d.refundVnd)}`)
  return phan.filter((x) => x !== '').join(' · ')
}

function LichSu({
  api,
  customerId,
  loai,
  goi,
  choSua,
  onLoi,
  onDaSua,
}: {
  api: Api
  customerId: string
  loai: 'goi' | 'vi'
  goi?: GoiKhach
  choSua: boolean
  onLoi: (s: string) => void
  onDaSua: () => void
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [dong, setDong] = useState<DongSoCai[] | null>(null)
  const [sua, setSua] = useState<DongSoCai | null>(null)
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const goiId = goi?.id

  const [hong, setHong] = useState(false)
  const nap = useCallback(async () => {
    setHong(false)
    try {
      setDong((await api.soCai(customerId, loai, goiId)).dong)
    } catch (e) {
      setHong(true)
      onLoi(loiCua(e))
    }
  }, [api, customerId, loai, goiId, onLoi])
  useEffect(() => {
    void nap()
  }, [nap])

  const suaDuoc = (d: DongSoCai) =>
    choSua &&
    !d.daDao &&
    (loai === 'goi' ? ['use', 'grant'] : ['opening', 'topup', 'spend']).includes(d.kind)

  async function suaSai() {
    if (sua === null) return
    setDang(true)
    try {
      await api.daoDong({ loai, dongId: sua.id, note: lyDo.trim() }, khoa)
      lamMoiKhoa()
      setSua(null)
      setLyDo('')
      onDaSua()
      await nap()
    } catch (e) {
      onLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  return (
    <div className="to-than">
      {goi !== undefined && (
        <p className="chu-phu">
          <b>{goi.name}</b> · {G.sessionsLeft(goi.sessionsLeft, goi.sessionsTotal)}
        </p>
      )}
      {dong === null && hong ? (
        <ThuLai onThuLai={() => void nap()} />
      ) : dong === null ? (
        <Xuong cao={200} />
      ) : (
        <ul className="ds-so-cai">
          {dong.map((d) => (
            <li key={d.id} className={d.daDao ? 'da-dao' : ''}>
              <div className="sc-dong-1">
                <b>{G.kind[d.kind] ?? d.kind}</b>
                <span className="sc-so">{moTaDong(d, loai)}</span>
              </div>
              <div className="sc-dong-2">
                <span>
                  <CacCum cac={[lucViet(d.luc), d.nguoi !== null ? G.by(d.nguoi) : null, d.note]} />
                </span>
                {d.daDao ? (
                  <span className="nhan-nho">{G.alreadyReversed}</span>
                ) : (
                  suaDuoc(d) && (
                    <button
                      type="button"
                      className="nut nut-nhat nut-nho"
                      onClick={() => setSua(d)}
                    >
                      <Undo2 size={14} />
                      {G.reverse}
                    </button>
                  )
                )}
              </div>
              {sua?.id === d.id && (
                <div className="sc-sua">
                  <p className="chu-phu">{G.reverseHint}</p>
                  <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonReverse} />
                  <div className="hang-chip" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="nut nut-nhat nut-nho"
                      onClick={() => setSua(null)}
                    >
                      {T.cancel}
                    </button>
                    <button
                      type="button"
                      className="nut nut-nguy nut-nho"
                      disabled={lyDo.trim().length < 2 || dang}
                      onClick={() => void suaSai()}
                    >
                      {G.reverseTitle}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Một dòng "lần nhập trước" trong khung cảnh báo nhập trùng. */
function moTaDaCo(d: SoDuCuDaCo): string {
  if (d.loai === 'vi') {
    return G.openingDaCoVi(tienVnd(d.cashVnd), d.bonusVnd === '0' ? null : tienVnd(d.bonusVnd))
  }
  return d.tang
    ? G.openingDaCoGoiTang(d.ten, d.soBuoi)
    : G.openingDaCoGoi(d.ten, d.soBuoi, tienVnd(d.giaTriVnd))
}

/** Trần một lần nhập (khớp máy chủ) — chỉ để hiện chữ; máy chủ vẫn kiểm. */
const TRAN_SO_DU_CU = 100_000_000n

function SoDuCu({
  api,
  customerId,
  branchId,
  onLoi,
  onXong,
  onHuy,
}: {
  api: Api
  customerId: string
  branchId: string
  onLoi: (s: string) => void
  onXong: Xong
  /** "Không nhập nữa": về tổng quan của khách, không ghi gì. */
  onHuy: () => void
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [dv, setDv] = useState<ServiceOption[]>([])
  /** Gói mẫu đang bán — chỉ để gợi ý giá trị còn lại; không có thì gợi ý theo giá lẻ. */
  const [goiMau, setGoiMau] = useState<GoiMau[]>([])
  const [variant, setVariant] = useState('')
  const [soBuoi, setSoBuoi] = useState('')
  const [giaTri, setGiaTri] = useState('')
  /** Người nhập đã tự gõ vào ô giá trị: không tự điền đè lên nữa. */
  const [giaTriDaSua, setGiaTriDaSua] = useState(false)
  const [goiTang, setGoiTang] = useState(false)
  const [het, setHet] = useState(themNgay(ngayVn(), 180))
  const [that, setThat] = useState('')
  const [tangVi, setTangVi] = useState('')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  /** Khách đã có số dư cũ cùng loại (409 OPENING_EXISTS): hỏi lại trước khi nhập thêm. */
  const [daCo, setDaCo] = useState<{ dong: SoDuCuDaCo[]; soKhac: number } | null>(null)
  useEffect(() => {
    void api
      .services()
      .then((r) => setDv(r.services))
      .catch((e: unknown) => onLoi(loiCua(e)))
    // Không có gói mẫu (hoặc không đọc được) thì gợi ý theo giá lẻ — không phải lỗi.
    void api
      .goiMauDangBan()
      .then((r) => setGoiMau(r.goi))
      .catch(() => setGoiMau([]))
  }, [api, onLoi])

  const soBuoiSo = /^\d{1,3}$/.test(soBuoi) ? Number.parseInt(soBuoi, 10) : 0
  const dvChon = dv.find((x) => x.id === variant)
  const goiY =
    dvChon === undefined || soBuoiSo < 1
      ? null
      : goiYGiaTriSoCu(
          soBuoiSo,
          vnd(dvChon.priceVnd),
          goiMau
            .filter((g) => g.serviceVariantId === variant && g.isActive)
            .map(
              (g): GoiMauGoiY => ({
                priceVnd: vnd(g.priceVnd),
                tongBuoi: g.paidSessions + g.bonusSessions,
              }),
            ),
        )
  const goiYChuoi = goiY !== null && goiY.giaTriVnd > 0n ? goiY.giaTriVnd.toString() : ''
  // Điền sẵn giá trị gợi ý cho tới khi người nhập tự gõ (hoặc tích Gói tặng).
  useEffect(() => {
    if (!giaTriDaSua && !goiTang) setGiaTri(goiYChuoi)
  }, [goiYChuoi, giaTriDaSua, goiTang])

  const form = {
    branchId,
    lyDo,
    variant,
    soBuoi,
    giaTri,
    goiTang,
    het,
    homNay: ngayVn(),
    that,
    tangVi,
  }
  const than = thanSoDuCu(form)
  const coGoi = variant !== '' && soBuoiSo > 0
  const giaTriSo = /^\d{1,12}$/.test(giaTri) ? BigInt(giaTri) : null
  const quaTran = giaTriSo !== null && giaTriSo > TRAN_SO_DU_CU
  const thieuGiaTri = coGoi && !goiTang && giaTri.trim() === ''
  const loiNho = loiGiaTriForm({ variant, soBuoi, giaTri, goiTang })
  const goiYSo = goiY === null ? 0n : goiY.giaTriVnd
  const lech =
    loiNho === null &&
    coGoi &&
    !goiTang &&
    giaTriSo !== null &&
    giaTriSo > 0n &&
    goiYSo > 0n &&
    (giaTriSo * 2n < goiYSo || giaTriSo > goiYSo * 2n)

  // Sửa bất kỳ ô nào thì khung "đã có số dư cũ" hết đúng — ẩn đi (khoá chống lặp giữ nguyên: chưa lưu gì).
  // biome-ignore lint/correctness/useExhaustiveDependencies: chỉ cần chạy khi các ô đổi
  useEffect(() => {
    setDaCo(null)
  }, [variant, soBuoi, giaTri, goiTang, het, that, tangVi, lyDo])

  async function luu(xacNhanThem: boolean) {
    const t = thanSoDuCu({ ...form, xacNhanThem })
    if (t === null) return
    setDang(true)
    try {
      await api.soDuCu(customerId, t, khoa)
      lamMoiKhoa()
      onXong(G.openingSaved)
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'OPENING_EXISTS') {
        setDaCo({ dong: e.daCo, soKhac: e.soKhac })
      } else {
        onLoi(loiCua(e))
      }
    } finally {
      setDang(false)
    }
  }

  const cauDaCo =
    daCo === null || daCo.dong[0] === undefined
      ? ''
      : G.openingDaCo(
          ngayViet(daCo.dong[0].ngay),
          daCo.dong.map(moTaDaCo).join('; ') +
            (daCo.soKhac > 0 ? `, ${G.openingDaCoKhac(daCo.soKhac)}` : ''),
        )

  return (
    <>
      <div className="to-than">
        <p className="chu-phu">{G.openingHint}</p>
        <div className="muc-to">
          <h3>{G.openingPackage}</h3>
          <select
            className="o-chon"
            aria-label={G.openingService}
            value={variant}
            onChange={(e) => setVariant(e.target.value)}
          >
            <option value="">{G.openingService}</option>
            {dv.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="luoi-2">
            <div className="truong">
              <label className="nhan" htmlFor="sdc-buoi">
                {G.openingSessions}
              </label>
              <input
                id="sdc-buoi"
                className="o-nhap"
                inputMode="numeric"
                value={soBuoi}
                onChange={(e) => setSoBuoi(e.target.value.replace(/\D/g, '').slice(0, 3))}
              />
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="sdc-han">
                {G.openingExpiry}
              </label>
              <ONgay id="sdc-han" giaTri={het} onDoi={setHet} />
            </div>
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="sdc-gia-tri">
              {G.openingValue}
            </label>
            <OTien
              id="sdc-gia-tri"
              giaTri={goiTang ? '0' : giaTri}
              onDoi={(x) => {
                setGiaTri(x)
                setGiaTriDaSua(true)
              }}
              khoa={goiTang}
            />
            {!goiTang && goiY !== null && goiYSo > 0n && dvChon !== undefined && (
              <>
                <p className="chu-phu chu-16">
                  {goiY.nguon === 'goi-mau'
                    ? G.openingValueGoiYMau(soBuoiSo, tienVnd(goiYChuoi))
                    : G.openingValueGoiY(soBuoiSo, tienVnd(dvChon.priceVnd))}
                </p>
                <p className="chu-phu chu-16">{G.openingValueGiam}</p>
              </>
            )}
            {thieuGiaTri && <p className="chu-loi">{G.openingValueThieu}</p>}
            {quaTran && <p className="chu-loi">{G.openingValueTran}</p>}
            {loiNho !== null && (
              <p className="chu-loi">{G.openingValueNho(soBuoiSo, tienVnd(loiNho.toiThieuVnd))}</p>
            )}
            {lech && (
              <ThongBao loai="canh-bao" lon>
                {G.openingValueLech(tienVnd(goiYChuoi))}
              </ThongBao>
            )}
          </div>
          {coGoi && (
            <label className="cong-tac-dong tinh-nang-dong cong-tac-goi-tang">
              <span className="chu">
                <b>{G.openingGift}</b>
                <span>{G.openingGiftHint}</span>
              </span>
              <input
                type="checkbox"
                checked={goiTang}
                onChange={(e) => setGoiTang(e.target.checked)}
              />
            </label>
          )}
        </div>
        <div className="muc-to">
          <h3>{G.openingWallet}</h3>
          <div className="luoi-2">
            <div className="truong">
              <label className="nhan" htmlFor="sdc-that">
                {G.openingCash}
              </label>
              <OTien id="sdc-that" giaTri={that} onDoi={setThat} />
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="sdc-tang">
                {G.openingBonus}
              </label>
              <OTien id="sdc-tang" giaTri={tangVi} onDoi={setTangVi} />
            </div>
          </div>
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={G.reasonOpening} />
        </div>
      </div>
      {daCo === null ? (
        <footer className="to-chan">
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            disabled={than === null || dang}
            onClick={() => void luu(false)}
          >
            {G.openingBtn}
          </button>
        </footer>
      ) : (
        <footer className="to-chan to-chan-nhieu-hang">
          <ThongBao loai="canh-bao" lon>
            {cauDaCo}
          </ThongBao>
          <button type="button" className="nut nut-vien nut-lon" disabled={dang} onClick={onHuy}>
            {G.openingStop}
          </button>
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            disabled={than === null || dang}
            onClick={() => void luu(true)}
          >
            {G.openingAddMore}
          </button>
        </footer>
      )}
    </>
  )
}

// =================================================================== Hoàn tất lịch = thu tiền (2 chạm)

/**
 * Tờ "Hoàn tất lịch" = THU TIỀN (Pha C, ADR-043): mở khi lễ tân bấm "Xong". Gói phù hợp (cùng khách +
 * dịch vụ, hạn gần nhất) được CHỌN SẴN; ví bật sẵn nếu đủ trả; cách trả mặc định tiền mặt. Một chạm
 * "Thu …" là xong: máy chủ ghi hoá đơn + trừ gói / ví + hoa hồng trong một giao dịch.
 * Giảm giá là lối phụ (bấm mới hiện), bắt buộc lý do.
 */
export function ToHoanTat({
  api,
  appointmentId,
  tenKhachHang,
  onDong,
  onXong,
  onLoi,
  onKhaiTaiKhoan,
}: {
  api: Api
  appointmentId: string
  tenKhachHang: string
  onDong: () => void
  /** Câu báo cho lễ tân: số hoá đơn, đã thu bao nhiêu, bằng gì. */
  onXong: (thongBao: string) => void
  onLoi: (s: string) => void
  /** Chỉ CHỦ tiệm có (v0.23.2): nút "Khai tài khoản nhận tiền" khi tiệm chưa có QR. Bấm → đóng tờ (không ghi gì) rồi mở Cấu hình. */
  onKhaiTaiKhoan?: () => void
}) {
  // Khoá trong ref: một khoá cho cả lần mở tờ, đổi khi máy chủ báo thành công (B5).
  const khoa = useRef(crypto.randomUUID())
  const [ph, setPh] = useState<GoiPhuHop | null>(null)
  /** itemId → packageId ('' = trả tiền như thường). */
  const [chon, setChon] = useState<Record<string, string>>({})
  /** null = tự động (bật khi ví đủ trả phần trả thường); true/false = lễ tân đã tự bấm. */
  const [dungViTay, setDungViTay] = useState<boolean | null>(null)
  const [hinhThuc, setHinhThuc] = useState<HinhThucThu>('cash')
  /** KH-GĐ2 5.1: `null` = trả một cách; có giá trị = số tiền từng cách (tổng phải bằng phần quầy thu). */
  const [chia, setChia] = useState<ChiaTien | null>(null)
  /** KH-GĐ2 5.2: tiền bo (chỉ khi bật công tắc "Tiền bo"). */
  const coTienBo = useTinhNang('tien-bo')
  const [bo, setBo] = useState<BoNhap>(() => boMoi([]))
  const [moGiam, setMoGiam] = useState(false)
  const [giam, setGiam] = useState('')
  const [lyDoGiam, setLyDoGiam] = useState('')
  /** KH-GĐ2 6.2.4: máy chủ báo giảm vượt trần → hiện ô mã chủ tiệm (máy khách không tự tính trần). */
  const [canMaChu, setCanMaChu] = useState(false)
  const [maChu, setMaChu] = useState('')
  /** KH-GĐ2 GĐ3: "Khách có ưu đãi …" — chọn SẴN khi khách có ưu đãi (Mini App hứa "tự áp khi bạn tới tiệm"); lễ tân
   *  chạm để bỏ nếu khách không muốn dùng lần này. Máy chủ vẫn tự kiểm + tự tính. */
  const [apUuDai, setApUuDai] = useState(false)
  const [dang, setDang] = useState(false)
  /** Lỗi hiện NGAY TRONG tờ (ui-critic v11: lỗi ở băng phía sau lớp phủ, tờ đứng im). */
  const [loiTo, setLoiTo] = useState<string | null>(null)
  /** Pha D: sản phẩm bán kèm (lối phụ — bấm mới nạp danh sách). */
  const [dsSp, setDsSp] = useState<SanPhamKho[] | null>(null)
  const [chonSp, setChonSp] = useState<Record<string, number>>({})
  /** Pha E: đổi điểm lấy giảm giá — bật thì đổi tối đa số điểm vừa với phần phải trả. */
  const [dungDiem, setDungDiem] = useState(false)
  // ADR-074: tắt Kho → không bán kèm sản phẩm; tắt Điểm → không đổi điểm (máy chủ cũng chặn).
  const coKho = useTinhNang('kho')
  const coDiemTn = useTinhNang('diem')
  const coHoaHong = useTinhNang('hoa-hong')

  useEffect(() => {
    void api
      .goiPhuHop(appointmentId)
      .then((r) => {
        const macDinh: Record<string, string> = {}
        for (const d of r.dichVu) {
          if (d.daTruGoi !== null) continue
          macDinh[d.itemId] = d.goi.find((g) => g.trangThai === 'con')?.id ?? ''
        }
        setChon(macDinh)
        setApUuDai(r.uuDai != null)
        setPh(r)
      })
      .catch((e: unknown) => {
        onLoi(loiCua(e))
        onDong()
      })
  }, [api, appointmentId, onLoi, onDong])
  // Máy chủ báo cần mã duyệt: ô mã nằm dưới cùng tờ — đưa vào tầm mắt + đặt con trỏ (miniapp-ux-reviewer GĐ3).
  useEffect(() => {
    if (!canMaChu) return
    const o = document.getElementById('ma-chu-duyet')
    o?.scrollIntoView({ block: 'center' })
    o?.focus()
  }, [canMaChu])

  if (ph === null) return null

  // Chỉ để HIỂN THỊ — cùng công thức với máy chủ (thu-tien.ts): tổng dịch vụ trả tiền − giảm giá
  // − ví đã trừ TỪ TRƯỚC (lần hoàn tất cũ) − ví trừ lần này = khách trả tại quầy. Số này được gửi lên
  // (`duKienVnd`); máy chủ tính lệch thì trả 409 và KHÔNG ghi gì (money-auditor BLOCKER 1).
  const khongDungGoi = ph.dichVu.filter((d) => d.daTruGoi === null && (chon[d.itemId] ?? '') === '')
  const tienDichVu = BigInt(congVndChuoi(khongDungGoi.map((d) => d.priceVnd)))
  const tienSanPham = dsSp === null ? 0n : tongSanPham(dsSp, chonSp)
  const soSanPham = Object.values(chonSp).reduce((t, n) => t + n, 0)
  // Ưu đãi khách mới: cùng hàm với máy chủ, chỉ trên dịch vụ trả tiền (không dòng trừ gói, không sản phẩm).
  // Số giảm NẾU áp (để hiện trên dòng công tắc) — chỉ trừ vào tiền khi lễ tân để công tắc bật.
  const giamUuDaiCoThe =
    ph.uuDai != null
      ? tinhGiamUuDai(
          dieuKhoanTuChuoi(ph.uuDai.dieuKhoan),
          khongDungGoi.map((d) => ({
            serviceVariantId: d.serviceVariantId ?? null,
            priceVnd: BigInt(d.priceVnd),
            traTien: true,
          })),
        )
      : 0n
  const giamUuDai = apUuDai ? giamUuDaiCoThe : 0n
  const phaiTra = tienDichVu + tienSanPham - giamUuDai
  const uuDaiApId = giamUuDai > 0n ? (ph.uuDai?.id ?? null) : null
  const daTruTruoc = BigInt(ph.daTruViVnd)
  const giamVnd = moGiam && giam !== '' ? BigInt(giam) : 0n
  const giamQuaTay = giamVnd > phaiTra - daTruTruoc
  const truocDiem = giamQuaTay ? 0n : phaiTra - giamVnd - daTruTruoc
  // Điểm: đổi tối đa số điểm mà giá trị không vượt phần còn phải trả (máy chủ kiểm lại đủ điểm).
  const motDiem =
    !coDiemTn || ph.diem === null || ph.diem === undefined ? 0n : BigInt(ph.diem.motDiemVnd)
  const coDiem = ph.diem === null || ph.diem === undefined ? 0n : BigInt(ph.diem.co)
  const diemToiDa =
    motDiem === 0n ? 0n : coDiem < truocDiem / motDiem ? coDiem : truocDiem / motDiem
  const doiDiem = dungDiem ? diemToiDa : 0n
  const giamDiemVnd = doiDiem * motDiem
  const sauGiam = truocDiem - giamDiemVnd
  // N2: cọc đang giữ trừ TRƯỚC ví — cùng thứ tự với máy chủ (taoHoaDon / hoanTatLich); lệch là 409.
  const cocGiu = BigInt(ph.cocGiuVnd ?? '0')
  const { cocVnd: truCoc, quayThuVnd: sauCoc } = thuTuTra({
    phaiTraVnd: sauGiam,
    cocGiuVnd: cocGiu,
    viCoVnd: 0n,
  })
  const tongVi = ph.vi === null ? 0n : BigInt(ph.vi.cashVnd) + BigInt(ph.vi.bonusVnd)
  const coTheTruVi = ph.vi !== null && tongVi > 0n && sauCoc > 0n
  // Có sản phẩm bán kèm thì KHÔNG tự trừ ví — lễ tân hỏi khách rồi tự bật (ui-critic v12).
  const dungVi = coTheTruVi && (dungViTay ?? (tongVi >= sauCoc && tienSanPham === 0n))
  const truVi = thuTuTra({
    phaiTraVnd: sauGiam,
    cocGiuVnd: cocGiu,
    viCoVnd: dungVi ? tongVi : 0n,
  }).viVnd
  const conLai = sauCoc - truVi
  const soBuoiTru = Object.values(chon).filter((p) => p !== '').length
  const giamHopLe =
    !moGiam || giam === '' || (giamVnd % 1000n === 0n && !giamQuaTay && lyDoGiam.trim().length >= 2)

  const chiaDung = chia === null || conLai === 0n || chiaHopLe(chia, conLai)
  const nhanNut = [
    conLai > 0n
      ? chia !== null
        ? G.splitCollect(tienVnd(conLai.toString()))
        : G.collectBy(tienVnd(conLai.toString()), G.hinhThuc[hinhThuc] ?? '')
      : '',
    soBuoiTru > 0 ? G.finishSessions(soBuoiTru) : '',
    truVi > 0n ? G.finishWallet(tienVnd(truVi.toString())) : '',
  ]
    .filter((x) => x !== '')
    .join(' · ')

  async function thu() {
    setDang(true)
    setLoiTo(null)
    try {
      const r = await api.hoanTat(
        appointmentId,
        {
          dungGoi: Object.entries(chon)
            .filter(([, p]) => p !== '')
            .map(([itemId, packageId]) => ({ itemId, packageId })),
          dungVi,
          thuTien: {
            hinhThuc,
            giamVnd: giamVnd.toString(),
            lyDoGiam: giamVnd > 0n ? lyDoGiam.trim() : null,
            duKienVnd: conLai.toString(),
            sanPham: dsChon(chonSp),
            doiDiem: Number.parseInt(doiDiem.toString(), 10),
            ...(chia !== null && conLai > 0n ? { chia: cacPhanChia(chia) } : {}),
            ...(coTienBo && bo.bat ? { tienBo: boGui(bo) } : {}),
            ...(uuDaiApId !== null ? { uuDaiId: uuDaiApId } : {}),
            ...(canMaChu && maChu !== ''
              ? { xacNhanChu: { ma: maChu, giamVnd: giamVnd.toString() } }
              : {}),
          },
        },
        khoa.current,
      )
      khoa.current = crypto.randomUUID()
      // Câu báo lấy SỐ MÁY CHỦ đã ghi (tiền thu tại quầy + cách trả), không phải số tổng hoá đơn.
      onXong(
        r.hoaDon === null
          ? G.finished
          : r.hoaDon.conLaiVnd === '0'
            ? r.hoaDon.walletVnd !== '0'
              ? G.walletToast(r.hoaDon.number, tienVnd(r.hoaDon.walletVnd))
              : G.recordedToast(r.hoaDon.number)
            : G.paidToast(
                r.hoaDon.number,
                tienVnd(r.hoaDon.conLaiVnd),
                G.hinhThuc[r.hoaDon.payMethod] ?? '',
              ),
      )
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'CAN_CHU_DUYET') setCanMaChu(true)
      // Mã sai: xoá để chủ tiệm gõ lại (mã không ở lại trên màn hình).
      if (canMaChu) setMaChu('')
      setLoiTo(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  return (
    <To nhan={G.finishTitle} onDong={onDong}>
      <header className="to-dau">
        <div style={{ flex: 1 }}>
          <h2>{G.finishTitle}</h2>
          <p className="chu-phu">{tenKhachHang}</p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {loiTo !== null && (
          <ThongBao loai="loi" onDong={() => setLoiTo(null)}>
            {loiTo}
          </ThongBao>
        )}
        <KhoiLuuY luuY={ph.luuY} />
        {ph.dichVu.map((d) => (
          <div key={d.itemId} className="muc-to">
            <h3 className="ten-dv">
              {d.dichVu} · {tienVnd(d.priceVnd)}
            </h3>
            {d.daTruGoi !== null ? (
              <p className="chu-phu">{G.alreadyUsed}</p>
            ) : d.goi.every((g) => g.trangThai !== 'con') ? null : (
              <div className="ds-chon">
                {d.goi
                  .filter((g) => g.trangThai === 'con')
                  .map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      aria-pressed={chon[d.itemId] === g.id}
                      className={`muc-chon ${chon[d.itemId] === g.id ? 'dang-chon' : ''}`}
                      onClick={() => setChon((c) => ({ ...c, [d.itemId]: g.id }))}
                    >
                      <span className="cham-chon" />
                      <span className="chu">
                        <b>{G.usePackage}</b>
                        <span>{G.packageOption(g.name, g.sessionsLeft, hanDen(g.expiresAt))}</span>
                        {g.tang === true && coHoaHong && <span>{G.giftNoCommission}</span>}
                      </span>
                    </button>
                  ))}
                <button
                  type="button"
                  aria-pressed={(chon[d.itemId] ?? '') === ''}
                  className={`muc-chon ${(chon[d.itemId] ?? '') === '' ? 'dang-chon' : ''}`}
                  onClick={() => setChon((c) => ({ ...c, [d.itemId]: '' }))}
                >
                  <span className="cham-chon" />
                  <span className="chu">
                    <b>{G.payNormal}</b>
                  </span>
                  <span className="gia">{tienVnd(d.priceVnd)}</span>
                </button>
              </div>
            )}
          </div>
        ))}

        {/* KH-GĐ2 6.2.2: khách có ưu đãi hợp lệ → bật sẵn, một chạm để bỏ (máy chủ kiểm lại + tự tính). Cùng kiểu
            dòng công tắc với điểm / ví trên tờ này (ui-critic GĐ3). */}
        {ph.coUuDai === true && <ThongBao loai="thong-tin">{G.uuDaiNhoQuay}</ThongBao>}
        {ph.uuDai != null && (
          <label className="cong-tac-dong">
            <span className="chu">
              <b>
                <TicketPercent size={16} /> {G.uuDaiCo(ph.uuDai.moTa)}
              </b>
              <span>
                {giamUuDaiCoThe > 0n
                  ? G.uuDaiGiam(tienVnd(giamUuDaiCoThe.toString()))
                  : G.uuDaiKhongAp}
              </span>
            </span>
            <input
              type="checkbox"
              checked={apUuDai && giamUuDaiCoThe > 0n}
              disabled={giamUuDaiCoThe === 0n}
              onChange={(e) => setApUuDai(e.target.checked)}
            />
          </label>
        )}
        <div className="hang-phu-to">
          {phaiTra - daTruTruoc > 0n && (
            <div className="muc-to">
              {moGiam ? (
                <div className="khung-giam">
                  <label className="nhan" htmlFor="tien-giam">
                    {G.discountAmount}
                  </label>
                  <OTien
                    id="tien-giam"
                    giaTri={giam}
                    onDoi={(so) => {
                      setGiam(so)
                      // Mã duyệt gắn với ĐÚNG số chủ tiệm thấy: đổi số là phải duyệt lại (soát bảo mật GĐ3 N3).
                      setMaChu('')
                    }}
                    goiY={G.discountHint}
                  />
                  {giamQuaTay && <p className="chu-phu chu-loi">{G.discountTooBig}</p>}
                  {!giamQuaTay &&
                    ph.hoaHongTruocGiam === true &&
                    giamVnd * 2n > phaiTra - daTruTruoc && (
                      <ThongBao loai="canh-bao">{G.discountBig}</ThongBao>
                    )}
                  <OLyDo giaTri={lyDoGiam} onDoi={setLyDoGiam} goiY={G.reasonDiscount} />
                  {canMaChu && (
                    <div className="truong" style={{ marginTop: 12 }}>
                      <label className="nhan" htmlFor="ma-chu-duyet">
                        {G.maChuDuyet(tienVnd(giamVnd.toString()))}
                      </label>
                      {/* Ô chữ che bằng CSS, KHÔNG phải ô mật khẩu — trình duyệt không đề nghị lưu (GĐ3 N2). */}
                      <input
                        id="ma-chu-duyet"
                        className="o-nhap o-ma-an"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={12}
                        value={maChu}
                        onChange={(e) => setMaChu(e.target.value.replace(/\D/g, ''))}
                      />
                      <p className="chu-phu">{G.maChuDuyetPhu}</p>
                    </div>
                  )}
                  <button
                    type="button"
                    className="nut nut-nhat nut-nho"
                    onClick={() => {
                      setMoGiam(false)
                      setGiam('')
                      setLyDoGiam('')
                    }}
                  >
                    {G.removeDiscount}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="nut nut-vien nut-nho"
                  onClick={() => setMoGiam(true)}
                >
                  <Percent size={16} />
                  {G.addDiscount}
                </button>
              )}
            </div>
          )}

          {coKho && (
            <div className="muc-to">
              {dsSp === null ? (
                <button
                  type="button"
                  className="nut nut-vien nut-nho"
                  onClick={() =>
                    void api
                      .kho(null)
                      .then((r) => setDsSp(r.sanPham))
                      .catch((e: unknown) => setLoiTo(loiCua(e)))
                  }
                >
                  <ShoppingBag size={16} />
                  {K.banKem}
                </button>
              ) : (
                <>
                  <h3>{K.banKem}</h3>
                  <ChonSanPham ds={dsSp} chon={chonSp} onDoi={setChonSp} />
                </>
              )}
            </div>
          )}
        </div>

        {coDiemTn && ph.diem !== null && ph.diem !== undefined && ph.diem.co !== '0' && (
          <label className="cong-tac-dong">
            <span className="chu">
              <b>
                <Gift size={16} /> {CK.coDiem(ph.diem.co)}
              </b>
              <span>
                {diemToiDa > 0n
                  ? CK.doiDiem(diemToiDa.toString(), tienVnd((diemToiDa * motDiem).toString()))
                  : CK.khongDuDoi}
              </span>
            </span>
            <input
              type="checkbox"
              checked={dungDiem && diemToiDa > 0n}
              disabled={diemToiDa === 0n}
              onChange={(e) => setDungDiem(e.target.checked)}
            />
          </label>
        )}

        {coTheTruVi && ph.vi !== null && (
          <label className="cong-tac-dong">
            <span className="chu">
              <b>
                <CircleDollarSign size={16} /> {G.payByWallet}
              </b>
              <span>
                {ph.vi.bonusVnd === '0'
                  ? G.walletHas(tienVnd(ph.vi.cashVnd))
                  : G.walletHasSplit(tienVnd(ph.vi.cashVnd), tienVnd(ph.vi.bonusVnd))}
              </span>
            </span>
            <input
              type="checkbox"
              checked={dungVi}
              onChange={(e) => setDungViTay(e.target.checked)}
            />
          </label>
        )}

        {coTienBo && <PhanTienBo tho={ph.tho ?? []} gt={bo} onDoi={setBo} />}

        {conLai > 0n && (
          <PhanTraTaiQuay
            api={api}
            appointmentId={appointmentId}
            phaiThu={conLai}
            hinhThuc={hinhThuc}
            onDoiHinhThuc={setHinhThuc}
            chia={chia}
            onDoiChia={setChia}
            {...(onKhaiTaiKhoan === undefined
              ? {}
              : {
                  onKhaiTaiKhoan: () => {
                    onDong()
                    onKhaiTaiKhoan()
                  },
                })}
          />
        )}

        {phaiTra > 0n && (
          <div className="bang-tien">
            {tienDichVu > 0n && (
              <p>
                <span>{G.lineServices}</span>
                <b>{tienVnd(tienDichVu.toString())}</b>
              </p>
            )}
            {tienSanPham > 0n && (
              <p>
                <span>{G.lineProducts(soSanPham)}</span>
                <b>{tienVnd(tienSanPham.toString())}</b>
              </p>
            )}
            {daTruTruoc > 0n && (
              <p>
                <span>{G.walletBefore}</span>
                <b>−{tienVnd(daTruTruoc.toString())}</b>
              </p>
            )}
            {giamUuDai > 0n && (
              <p>
                <span>{G.uuDaiDong}</span>
                <b>−{tienVnd(giamUuDai.toString())}</b>
              </p>
            )}
            {giamVnd > 0n && !giamQuaTay && (
              <p>
                <span>{G.discountLine}</span>
                <b>−{tienVnd(giamVnd.toString())}</b>
              </p>
            )}
            {giamDiemVnd > 0n && (
              <p>
                <span>{CK.dongDiem(doiDiem.toString())}</span>
                <b>−{tienVnd(giamDiemVnd.toString())}</b>
              </p>
            )}
            {truCoc > 0n && (
              <p>
                <span>{G.dueDeposit}</span>
                <b>−{tienVnd(truCoc.toString())}</b>
              </p>
            )}
            {truVi > 0n && (
              <p>
                <span>{G.dueWallet}</span>
                <b>−{tienVnd(truVi.toString())}</b>
              </p>
            )}
            <p className="dong-phai-tra">
              <span>{G.dueCounter}</span>
              <b>{tienVnd(conLai.toString())}</b>
            </p>
          </div>
        )}
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={dang || !giamHopLe || !chiaDung || (coTienBo && !boHopLe(bo))}
          onClick={() => void thu()}
        >
          {dang
            ? G.finishing
            : nhanNut === ''
              ? `${G.finishBtn} · ${G.recordOnly}`
              : `${G.finishBtn} · ${nhanNut}`}
        </button>
      </footer>
    </To>
  )
}

/** a < b cho hai chuỗi số nguyên đồng không âm — so độ dài rồi so chuỗi, không qua Number (CLAUDE.md §1). */
function nhoHonChuoi(a: string, b: string): boolean {
  return a.length !== b.length ? a.length < b.length : a < b
}

// =================================================================== Trang "Khách & gói"

export function TrangKhach({
  toi,
  api,
  onDatLich,
  onMoDichVu,
  theDau = 'tim',
}: {
  toi: ToiLaAi
  api: Api
  /** Chuyển sang màn Lịch và mở tờ đặt lịch với số điện thoại này. */
  onDatLich?: (sdt: string) => void
  /** Sang Cấu hình → Dịch vụ (sửa chu kỳ quay lại) — chỉ người cấu hình được tiệm. */
  onMoDichVu?: () => void
  /** Mở thẳng thẻ nào (chuông thông báo "khách xin chờ" mở thẳng Chờ chỗ). */
  theDau?: 'tim' | 'cham' | 'cho'
}) {
  const [the, setThe] = useState<'tim' | 'cham' | 'cho'>(theDau)
  const coGoiVi = useTinhNang('goi-vi')
  const [sdt, setSdt] = useState('')
  const [kq, setKq] = useState<CustomerHit[] | null>(null)
  const [moId, setMoId] = useState<string | null>(null)
  /** STT 2.1 / 2.2: tờ "Thêm" → chọn → Thêm khách / Nhập danh sách (chỉ chủ tiệm). */
  const [them, setThem] = useState<'chon' | 'them' | 'nhap' | null>(null)
  const [dienSan, setDienSan] = useState<Partial<FormKhach>>({})
  /** Câu báo "Đã thêm khách…" chuyển vào hồ sơ vừa mở (`baoBanDau`) — không để lại ở trang phía sau. */
  const [baoHoSo, setBaoHoSo] = useState<{ id: string; cau: string } | null>(null)
  /** Tăng lên để tìm lại (vừa thêm / nhập khách xong). */
  const [lamMoi, setLamMoi] = useState(0)
  const quyenK = quyenKhach({ vai: toi.toi.vai, quyen: toi.quyen })
  const cacThe: { ma: 'tim' | 'cham' | 'cho'; ten: string }[] = [
    { ma: 'tim', ten: CK.tabTim },
    { ma: 'cham', ten: CK.tabCham },
    ...(toi.quyen.includes('dat-lich') ? [{ ma: 'cho' as const, ten: CK.tabCho }] : []),
  ]
  /** ADR-101: bán gói / nạp ví / hoàn từ hồ sơ khách ghi vào ĐÚNG chi nhánh đang dùng (trước đây luôn là chi nhánh đầu). */
  const chiNhanh = useChiNhanh()
  const chiNhanhHoSo = chiNhanhChoHoSoKhach(chiNhanh.id)
  const [loi, setLoi] = useState<string | null>(null)
  // Đổi chi nhánh giữa chừng (hiếm: chi nhánh đang dùng vừa bị ẩn): đóng hồ sơ / tờ đang mở, không để ghi tiền sang chi nhánh khác.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `chiNhanh.id` là TÍN HIỆU chạy lại.
  useEffect(() => {
    setMoId(null)
    setThem(null)
  }, [chiNhanh.id])

  // KH-GĐ2 4.3: tên / 3–4 số cuối / số đủ; ô trống = 10 khách vừa tới. Chờ 250 ms sau lần gõ cuối.
  const goVao = sdt.trim()
  const laSo = /^[\d\s+.-]+$/.test(goVao)
  const duDeTim =
    goVao === '' || (laSo ? goVao.replace(/\D/g, '').length >= 3 : [...goVao].length >= 2)
  // `lamMoi` chỉ để tìm lại sau khi thêm / nhập khách — không dùng trong thân.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tăng `lamMoi` = tìm lại ngay
  useEffect(() => {
    setKq(null)
    if (!duDeTim) return
    let con = true
    const t = setTimeout(() => {
      void api
        .findCustomer(goVao)
        .then((r) => {
          if (con) setKq(r.customers)
        })
        .catch((e: unknown) => {
          if (con) setLoi(loiCua(e))
        })
    }, 250)
    return () => {
      con = false
      clearTimeout(t)
    }
  }, [api, goVao, duDeTim, lamMoi])

  return (
    <>
      <header className="dau-man">
        <div className="ten-man">
          <DongTenTiem tenTiem={toi.tenTiem} />
          <h1>{coGoiVi ? G.pageCustomer : G.pageCustomerGon}</h1>
        </div>
      </header>
      <main className="than-man trang-khach">
        <div className="phan-doan phan-doan-deu" role="tablist" aria-label={G.pageCustomer}>
          {cacThe.map((t) => (
            <button
              key={t.ma}
              type="button"
              role="tab"
              aria-selected={the === t.ma}
              className={the === t.ma ? 'dang-chon' : ''}
              onClick={() => setThe(t.ma)}
            >
              {t.ten}
            </button>
          ))}
        </div>
        {the === 'cham' && (
          <TheChamKhach api={api} {...(onMoDichVu === undefined ? {} : { onMoDichVu })} />
        )}
        {the === 'cho' && <TheChoCho api={api} />}
        {the === 'tim' && (
          <>
            <section className="the the-dem tim-khach">
              <label className="nhan" htmlFor="tim-sdt">
                {G.searchPhone}
              </label>
              <div className="hang-tim-them">
                <div className="o-nhap-icon">
                  <Search size={20} />
                  <input
                    id="tim-sdt"
                    className="o-nhap"
                    type="text"
                    enterKeyHint="search"
                    autoComplete="off"
                    placeholder={G.searchPlaceholder}
                    value={sdt}
                    onChange={(e) => setSdt(e.target.value)}
                  />
                </div>
                {quyenK.coThem && (
                  <button
                    type="button"
                    className="nut nut-vien nut-form nut-them-khach"
                    onClick={() => {
                      setDienSan({})
                      setThem(quyenK.coNhap ? 'chon' : 'them')
                    }}
                  >
                    <UserPlus size={18} />
                    {KH.them}
                  </button>
                )}
              </div>
              <p className="chu-phu">{G.searchHint}</p>
            </section>
            {/* 0034: Zalo gắn theo số chờ quầy xác nhận đúng người — chỉ hiện khi có, gập một dòng, SAU ô tìm (vòng 39). */}
            <TheXacNhanZalo api={api} />
            {loi !== null && (
              <ThongBao loai="loi" onDong={() => setLoi(null)}>
                {loi}
              </ThongBao>
            )}
            {kq !== null &&
              (kq.length === 0 ? (
                <div className="the">
                  <Rong
                    icon={<Search size={26} />}
                    tieuDe={G.noCustomerFound}
                    moTa={G.noCustomerHint}
                  />
                  {/* Tìm không ra thì luôn có lối đi tiếp (ui-critic v28): số đúng dạng → đặt cho số đó. */}
                  {(onDatLich !== undefined || quyenK.coThem) && (
                    <div className="hang-giua hang-giua-nut">
                      {onDatLich !== undefined && (
                        <button
                          type="button"
                          className="nut nut-chinh nut-form"
                          onClick={() =>
                            onDatLich(chuanHoaSdt(goVao) === null ? '' : goVao.replace(/\D/g, ''))
                          }
                        >
                          <CalendarPlus size={18} />
                          {chuanHoaSdt(goVao) === null ? G.bookNewCustomer : G.bookForPhone}
                        </button>
                      )}
                      {quyenK.coThem && (
                        <button
                          type="button"
                          className="nut nut-vien nut-form"
                          onClick={() => {
                            setDienSan(dienSanTuO(goVao))
                            setThem('them')
                          }}
                        >
                          <UserPlus size={18} />
                          {KH.themKhach}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {goVao === '' && <h2 className="tieu-de-muc">{G.searchRecent}</h2>}
                  <ul className="the ds-khach">
                    {kq.map((k) => (
                      <li key={k.id}>
                        <button type="button" className="dong-khach" onClick={() => setMoId(k.id)}>
                          <span className="chu">
                            <b>{tenKhach(k.fullName)}</b>
                            <span>
                              {/* "dd/MM" — năm làm dòng bẻ ở khổ 360 (ui-critic v29). */}
                              <CacCum
                                cac={[
                                  // Khách Zalo chưa cho số: nói rõ "chưa có số", đừng để dòng trông như bị mất số.
                                  k.phone === null || k.phone === ''
                                    ? KH.chuaCoSo
                                    : hienSdt(k.phone),
                                  goVao === '' ? cauMocGanDay(k) : null,
                                ]}
                              />
                            </span>
                          </span>
                          <span className="nut nut-buoc nut-nho">
                            {coGoiVi ? <Wallet size={16} /> : <UserRound size={16} />}
                            {coGoiVi ? G.openKhach : G.openKhachGon}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ))}
          </>
        )}
      </main>
      {them === 'chon' && (
        <ToChonThem
          onDong={() => setThem(null)}
          onThemKhach={() => setThem('them')}
          onNhapDs={() => setThem('nhap')}
        />
      )}
      {them === 'them' && (
        <ToThemKhach
          api={api}
          dienSan={dienSan}
          onDong={() => setThem(null)}
          onMoHoSo={(id) => {
            setThem(null)
            setMoId(id)
          }}
          onXong={(k) => {
            setThem(null)
            const cau =
              k.sdtDaDoi && k.sdt !== null
                ? `${KH.daThem(k.ten)} ${KH.soCuDaDoi(giuSoLienMach(hienSdt(k.sdt)))}`
                : KH.daThem(k.ten)
            setLamMoi((n) => n + 1)
            // Mở thẳng hồ sơ vừa tạo để ghi ngày sinh / số dư cũ nếu cần — câu báo hiện NGAY TRONG hồ sơ đó
            // (đặt ở trang phía sau thì nằm dưới lớp phủ, không ai thấy).
            setBaoHoSo({ id: k.id, cau })
            setMoId(k.id)
          }}
        />
      )}
      {them === 'nhap' && (
        <NhapKhach
          api={api}
          onDong={() => setThem(null)}
          onXong={() => {
            setThem(null)
            setLamMoi((n) => n + 1)
          }}
        />
      )}
      {moId !== null && chiNhanhHoSo !== null && (
        <ToGoiViKhach
          key={moId}
          api={api}
          toi={toi}
          customerId={moId}
          branchId={chiNhanhHoSo}
          onDong={() => setMoId(null)}
          onMoKhac={setMoId}
          onDaGop={() => setLamMoi((n) => n + 1)}
          {...(baoHoSo !== null && baoHoSo.id === moId ? { baoBanDau: baoHoSo.cau } : {})}
        />
      )}
    </>
  )
}

// =================================================================== Cấu hình "Gói & ví"

type NhapGoi = {
  id: string | null
  serviceVariantId: string
  name: string
  paidSessions: string
  bonusSessions: string
  priceVnd: string
  validityDays: string
  isActive: boolean
}

const GOI_TRONG: NhapGoi = {
  id: null,
  serviceVariantId: '',
  name: '',
  paidSessions: '10',
  bonusSessions: '0',
  priceVnd: '',
  validityDays: '365',
  isActive: true,
}

export function TheGoiViCauHinh({ toi, api }: { toi: ToiLaAi; api: Api }) {
  const xemDoanhThu = toi.quyen.includes('xem.doanh-thu')
  const [goi, setGoi] = useState<GoiMau[] | null>(null)
  const [dv, setDv] = useState<ServiceOption[]>([])
  const [sua, setSua] = useState<NhapGoi | null>(null)
  const [muc, setMuc] = useState<{ tu: string; pt: string }[] | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [baoCao, setBaoCao] = useState<Awaited<ReturnType<Api['baoCaoGoiVi']>> | null>(null)

  const nap = useCallback(async () => {
    try {
      const [g, s, m] = await Promise.all([api.goiMauCauHinh(), api.services(), api.mucTang()])
      setGoi(g.goi)
      setDv(s.services)
      setMuc(m.mucTang.map((x: MucTangVi) => ({ tu: x.minTopupVnd, pt: String(x.bonusBp / 100) })))
      if (xemDoanhThu) {
        const hn = ngayVn()
        setBaoCao(await api.baoCaoGoiVi(`${hn.slice(0, 7)}-01`, hn))
      }
    } catch (e) {
      setLoi(loiCua(e))
    }
  }, [api, xemDoanhThu])
  useEffect(() => {
    void nap()
  }, [nap])

  async function luuGoi() {
    if (sua === null) return
    try {
      await api.luuGoiMau(sua.id, {
        serviceVariantId: sua.serviceVariantId,
        name: sua.name.trim(),
        paidSessions: Number.parseInt(sua.paidSessions, 10),
        bonusSessions: Number.parseInt(sua.bonusSessions === '' ? '0' : sua.bonusSessions, 10),
        priceVnd: sua.priceVnd,
        validityDays: Number.parseInt(sua.validityDays, 10),
        isActive: sua.isActive,
      })
      setSua(null)
      setBao(G.planSaved)
      await nap()
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  async function luuMuc() {
    if (muc === null) return
    try {
      // Phần trăm → basis point: 10 → 1000, 12,5 → 1250. Chấp nhận dấu phẩy kiểu Việt.
      const bp = (pt: string) => {
        const [nguyen = '0', le = ''] = pt.replace(',', '.').split('.')
        return Number.parseInt(nguyen, 10) * 100 + Number.parseInt((le + '00').slice(0, 2), 10)
      }
      await api.luuMucTang(
        muc.filter((m) => m.tu !== '').map((m) => ({ minTopupVnd: m.tu, bonusBp: bp(m.pt) })),
      )
      setBao(G.tiersSaved)
      await nap()
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  const goiHopLe =
    sua !== null &&
    sua.serviceVariantId !== '' &&
    sua.name.trim() !== '' &&
    Number.parseInt(sua.paidSessions, 10) > 0 &&
    sua.priceVnd !== '' &&
    sua.priceVnd.endsWith('000') &&
    Number.parseInt(sua.validityDays, 10) > 0

  return (
    <div className="goi-vi-cau-hinh">
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && (
        <ThongBao loai="loi" onDong={() => setLoi(null)}>
          {loi}
        </ThongBao>
      )}

      {xemDoanhThu && baoCao !== null && (
        <section className="ba-con-so">
          <div className="the the-dem so-lieu">
            <span className="nhan-muc">{G.collected}</span>
            <b>{tienVnd(baoCao.tienDaThu.tongVnd)}</b>
            <span className="chu-phu">
              <CacCum
                cac={G.collectedSubCum(
                  tienVnd(baoCao.tienDaThu.banGoiVnd),
                  tienVnd(baoCao.tienDaThu.napViVnd),
                  tienVnd(baoCao.tienDaThu.daHoanVnd),
                )}
              />
            </span>
          </div>
          <div className="the the-dem so-lieu">
            <span className="nhan-muc">{G.earned}</span>
            <b>{tienVnd(baoCao.doanhThuThucHien.tongVnd)}</b>
            <span className="chu-phu">
              <CacCum
                cac={G.earnedSubCum(
                  tienVnd(baoCao.doanhThuThucHien.tuGoiVnd),
                  tienVnd(baoCao.doanhThuThucHien.tuViVnd),
                )}
              />
            </span>
          </div>
          <div className="the the-dem so-lieu no-khach">
            <span className="nhan-muc">{G.owed}</span>
            <b>{tienVnd(baoCao.conNoKhach.tongVnd)}</b>
            <span className="chu-phu">
              <CacCum
                cac={G.owedSubCum(
                  baoCao.conNoKhach.soBuoi,
                  tienVnd(baoCao.conNoKhach.tienGoiVnd),
                  tienVnd(baoCao.conNoKhach.viThatVnd),
                )}
              />
            </span>
            {baoCao.conNoKhach.viTangVnd !== '0' && (
              <span className="chu-phu">{G.owedBonus(tienVnd(baoCao.conNoKhach.viTangVnd))}</span>
            )}
            {baoCao.conNoKhach.goiHetHanVnd !== '0' && (
              <span className="chu-phu">
                {G.owedExpired(tienVnd(baoCao.conNoKhach.goiHetHanVnd))}
              </span>
            )}
            <span className="chu-phu">{G.owedHint}</span>
          </div>
        </section>
      )}

      <section className="the the-dem">
        <div className="dau-muc">
          <div>
            <h2>{G.plansTitle}</h2>
            <p className="chu-phu">{G.plansHint}</p>
          </div>
          <button
            type="button"
            className="nut nut-vien nut-nho"
            disabled={sua !== null}
            onClick={() => setSua({ ...GOI_TRONG })}
          >
            <Plus size={16} />
            {G.addPlan}
          </button>
        </div>
        {goi === null ? (
          <Xuong cao={60} />
        ) : goi.length === 0 && sua === null ? (
          <Rong icon={<Package size={26} />} tieuDe={G.noPlan} moTa={G.plansHint} />
        ) : (
          <ul className="ds-goi-mau">
            {goi.map((g) => (
              <li key={g.id} className={g.isActive ? '' : 'tat'}>
                <button
                  type="button"
                  className="dong-goi-mau"
                  onClick={() =>
                    setSua({
                      id: g.id,
                      serviceVariantId: g.serviceVariantId,
                      name: g.name,
                      paidSessions: String(g.paidSessions),
                      bonusSessions: String(g.bonusSessions),
                      priceVnd: g.priceVnd,
                      validityDays: String(g.validityDays),
                      isActive: g.isActive,
                    })
                  }
                >
                  <span className="chu">
                    <b>{g.name}</b>
                    <span>
                      <CacCum
                        cac={[
                          g.dichVu,
                          G.planLine(g.paidSessions, g.bonusSessions),
                          G.planValidity(g.validityDays),
                          g.isActive ? null : G.planOff,
                        ]}
                      />
                    </span>
                  </span>
                  <span className="gia">{tienVnd(g.priceVnd)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {sua !== null && (
          <div className="form-goi">
            <h3>{sua.id === null ? G.addPlan : G.editPlan}</h3>
            <div className="truong">
              <label className="nhan" htmlFor="goi-dv">
                {G.planService}
              </label>
              <select
                id="goi-dv"
                className="o-chon"
                value={sua.serviceVariantId}
                onChange={(e) => setSua({ ...sua, serviceVariantId: e.target.value })}
              >
                <option value="">{G.planService}</option>
                {dv.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label} · {tienVnd(s.priceVnd)} · {formatDuration(s.durationMin)}
                  </option>
                ))}
              </select>
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="goi-ten">
                {G.planName}
              </label>
              <input
                id="goi-ten"
                className="o-nhap"
                placeholder={G.planNamePlaceholder}
                value={sua.name}
                maxLength={120}
                onChange={(e) => setSua({ ...sua, name: e.target.value })}
              />
            </div>
            <div className="luoi-3">
              {(
                [
                  ['paidSessions', G.planPaid, 3],
                  ['bonusSessions', G.planBonus, 2],
                  ['validityDays', G.planDays, 4],
                ] as const
              ).map(([k, nhan, dai]) => (
                <div key={k} className="truong">
                  <label className="nhan" htmlFor={`goi-${k}`}>
                    {nhan}
                  </label>
                  <input
                    id={`goi-${k}`}
                    className="o-nhap"
                    inputMode="numeric"
                    value={sua[k]}
                    onChange={(e) =>
                      setSua({ ...sua, [k]: e.target.value.replace(/\D/g, '').slice(0, dai) })
                    }
                  />
                </div>
              ))}
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="goi-gia">
                {G.planPrice}
              </label>
              <OTien
                id="goi-gia"
                giaTri={sua.priceVnd}
                onDoi={(v) => setSua({ ...sua, priceVnd: v })}
              />
            </div>
            <label className="cong-tac-dong">
              <span className="chu">
                <b>{G.planActive}</b>
              </span>
              <input
                type="checkbox"
                checked={sua.isActive}
                onChange={(e) => setSua({ ...sua, isActive: e.target.checked })}
              />
            </label>
            <div className="hang-chip">
              <button type="button" className="nut nut-nhat" onClick={() => setSua(null)}>
                {T.cancel}
              </button>
              <button
                type="button"
                className="nut nut-chinh"
                disabled={!goiHopLe}
                onClick={() => void luuGoi()}
              >
                {G.planSave}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="the the-dem">
        <h2>{G.tiersTitle}</h2>
        <p className="chu-phu">{G.tiersHint}</p>
        {muc === null ? (
          <Xuong cao={60} />
        ) : (
          <div className="ds-muc-tang">
            {muc.map((m, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: dòng nhập chưa có id; thứ tự là danh tính.
              <div key={i} className="dong-muc-tang">
                <div className="truong">
                  <label className="nhan" htmlFor={`muc-tu-${i}`}>
                    {G.tierFrom}
                  </label>
                  <OTien
                    id={`muc-tu-${i}`}
                    giaTri={m.tu}
                    onDoi={(v) => setMuc(muc.map((x, j) => (j === i ? { ...x, tu: v } : x)))}
                  />
                </div>
                <div className="truong o-phan-tram">
                  <label className="nhan" htmlFor={`muc-pt-${i}`}>
                    {G.tierPercent}
                  </label>
                  <input
                    id={`muc-pt-${i}`}
                    className="o-nhap"
                    inputMode="decimal"
                    value={m.pt}
                    onChange={(e) =>
                      setMuc(
                        muc.map((x, j) =>
                          j === i
                            ? { ...x, pt: e.target.value.replace(/[^\d,.]/g, '').slice(0, 5) }
                            : x,
                        ),
                      )
                    }
                  />
                </div>
                <button
                  type="button"
                  className="nut nut-nhat nut-icon"
                  aria-label={G.removeTier}
                  onClick={() => setMuc(muc.filter((_, j) => j !== i))}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <div className="hang-chip">
              {muc.length < 5 && (
                <button
                  type="button"
                  className="nut nut-vien nut-nho"
                  onClick={() => setMuc([...muc, { tu: '', pt: '5' }])}
                >
                  <Plus size={16} />
                  {G.addTier}
                </button>
              )}
              <button
                type="button"
                className={`nut nut-nho ${sua === null ? 'nut-chinh' : 'nut-vien'}`}
                onClick={() => void luuMuc()}
              >
                {G.tiersSave}
              </button>
            </div>
          </div>
        )}
      </section>

      {toi.quyen.includes('cau-hinh.nhan-vien') && <NhatKyTien api={api} />}
    </div>
  )
}

/** Nhật ký tiền cho CHỦ TIỆM: ai hoàn, ai sửa sai, ai tặng buổi… (security NẶNG 3, B5). */
function NhatKyTien({ api }: { api: Api }) {
  const [dong, setDong] = useState<DongNhatKyTien[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [het, setHet] = useState(false)
  useEffect(() => {
    void api
      .nhatKyTien()
      .then((r) => setDong(r.dong))
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])
  const hien = dong === null ? [] : het ? dong : dong.slice(0, 10)
  return (
    <section className="the the-dem">
      <h2>
        <ScrollText size={18} /> {G.moneyLogTitle}
      </h2>
      <p className="chu-phu">{G.moneyLogHint}</p>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {dong === null ? (
        loi === null && <Xuong cao={60} />
      ) : dong.length === 0 ? (
        <p className="chu-phu">{G.moneyLogEmpty}</p>
      ) : (
        <ul className="ds-so-cai">
          {hien.map((d) => (
            <li key={d.id}>
              <div className="sc-dong-1">
                <b>{G.moneyAction[d.action] ?? d.action}</b>
                {d.soTienVnd !== null && <span className="sc-so">{tienVnd(d.soTienVnd)}</span>}
              </div>
              <div className="sc-dong-2">
                <span>
                  <CacCum
                    cac={[
                      lucViet(d.luc),
                      d.nguoi !== null ? G.byRole(d.nguoi) : null,
                      d.tang ? G.moneyLogGift : null,
                      d.soTienTangVnd !== null && d.soTienTangVnd !== '0'
                        ? G.moneyLogBonus(tienVnd(d.soTienTangVnd))
                        : null,
                      d.lyDo,
                    ]}
                  />
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {dong !== null && dong.length > 10 && !het && (
        <button type="button" className="nut nut-nhat nut-nho" onClick={() => setHet(true)}>
          {G.moneyLogMore}
        </button>
      )}
    </section>
  )
}
